import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { PDFDocument, StandardFonts, rgb, PDFFont, PDFPage, PDFForm, PDFTextField, PDFCheckBox, degrees } from 'pdf-lib';
import { format } from 'date-fns';
import { formService } from './formService';
import { customUserDataFieldService } from './customUserDataFieldService';

export interface PDFGenerationOptions {
  filename?: string;
  headerConfigurationJson?: string; // JSON config for dynamic header generation
  headerHeight?: number;
  pageMargin?: number;
  contentMargin?: number;
  headerBottomMargin?: number;
  // Form layout configuration
  numberOfColumns?: number; // Number of columns in the form layout (default: 2)
  // Font size configuration
  defaultFontSize?: number; // Default font size for text fields (default: 7)
  labelFontSize?: number; // Font size for labels (default: 8)
  multilineFontSize?: number; // Font size for multiline fields (default: 7)
  sectionTitleFontSize?: number; // Font size for section titles (default: 12)
  // Padding and spacing configuration
  fieldSpacing?: number; // Spacing between fields (default: 3.5)
  sectionSpacing?: number; // Spacing between sections (default: 5)
  checkboxListSpacing?: number; // Extra spacing under checkbox lists (default: 5)
  checkboxListColumns?: number; // Number of columns for checkbox lists (default: 3)
  sectionPadding?: number; // Padding inside sections (default: 3)
  fieldHeight?: number; // Height of standard fields (default: 16)
  // Section styling
  showSectionBorders?: boolean; // Whether to show section borders (default: true)
  // Dynamic header data (for programmatic values)
  headerData?: { [key: string]: string | number | boolean | null | undefined }; // Data to populate header placeholders
  // Pixel Version flag
  pixelVersion?: boolean; // If true, use enhanced configuration from FormFields and FormSections (default: false)
  // Data for parameter preloading
  job?: any;
  customer?: any;
  formParameterValues?: { [fieldId: number]: string };
  userDataFields?: any[]; // Store fetched user data fields for parameter injection
}

// Header Configuration Interfaces
export interface HeaderConfiguration {
  type: 'dynamic' | 'image';
  height?: number;
  backgroundColor?: string;
  rightPadding?: number; // Padding from right edge for right-aligned elements
  elements: HeaderElement[];
}

export interface TextElement {
  type: 'text';
  content: string;
  fontSize: number;
  fontWeight?: 'normal' | 'bold';
  fontFamily?: string;
  color: string;
  x: 'center' | 'left' | 'right' | 'margin' | number;
  y: number;
  alignment?: 'left' | 'center' | 'right';
  maxWidth?: number;
}

export interface LineElement {
  type: 'line';
  x1: 'margin' | 'page-width-minus-margin' | number;
  y1: number;
  x2: 'margin' | 'page-width-minus-margin' | number;
  y2: number;
  thickness: number;
  color: string;
  dashPattern?: number[];
}

export interface RectangleElement {
  type: 'rectangle';
  x: number;
  y: number;
  width: number | 'page-width-minus-margin';
  height: number;
  fillColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

export type HeaderElement = TextElement | LineElement | RectangleElement;

export class PDFService {
  /**
   * Normalizes fieldType to handle both string and numeric enum values
   */
  private static normalizeFieldType(fieldType: any): string {
    // If it's already a string, return it
    if (typeof fieldType === 'string') {
      if (fieldType === 'Divider') return 'HorizontalRule';
      return fieldType;
    }
    
    // If it's a number (enum value), convert to string name
    const enumMap: { [key: number]: string } = {
      1: 'String',
      2: 'Multiline',
      3: 'Bool',
      4: 'Dimension',
      5: 'Int',
      6: 'Dropdown',
      7: 'Date',
      8: 'DateTime',
      9: 'CheckboxList',
      10: 'HorizontalRule',
      11: 'TextboxWithCheckbox'
    };
    
    return enumMap[fieldType as number] || 'String';
  }

  /**
   * Configuration for multiline fields
   */
  private static readonly DEFAULT_MULTILINE_FONT_SIZE = 7; // Font size for multiline fields
  private static readonly MULTILINE_LINE_SPACING = 2; // Spacing between lines
  private static readonly MULTILINE_LINES = 3; // Number of visible lines
  
  /**
   * Configuration for checkbox lists
   */
  private static readonly CHECKBOX_LIST_ROW_SPACING = 4; // Spacing between rows of checkbox list items (in points)

  /**
   * Calculates the height needed for a multiline field to show exactly N lines
   * @param multilineFontSize Font size for multiline fields (optional, defaults to DEFAULT_MULTILINE_FONT_SIZE)
   * @returns Height in points
   */
  private static calculateMultilineHeight(multilineFontSize?: number): number {
    const fontSize = multilineFontSize ?? this.DEFAULT_MULTILINE_FONT_SIZE;
    const lineHeight = fontSize + this.MULTILINE_LINE_SPACING;
    // For N visible lines: N * lineHeight (includes spacing between lines)
    // This ensures exactly N lines are visible in the field
    return this.MULTILINE_LINES * lineHeight;
  }

  /**
   * Calculates the height needed for a multiline field with custom number of lines
   * @param fontSize Font size for the multiline field
   * @param numLines Number of lines to display
   * @returns Height in points
   */
  private static calculateCustomMultilineHeight(fontSize: number, numLines: number): number {
    const lineHeight = fontSize + this.MULTILINE_LINE_SPACING;
    return numLines * lineHeight;
  }

  /**
   * Wraps text to fit within a maximum width
   */
  private static wrapText(text: string, maxWidth: number, font: PDFFont, fontSize: number): string[] {
    if (!text) return [];

    // Split by explicit newlines first
    const paragraphs = text.split(/\r?\n/);
    const allLines: string[] = [];

    for (const paragraph of paragraphs) {
      const words = paragraph.split(' ');
      let currentLine = '';

      for (const word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) allLines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) allLines.push(currentLine);
    }

    return allLines;
  }

  /**
   * Parses a field's configuration JSON into an object.
   */
  private static parseFieldConfiguration(field: any): Record<string, any> {
    if (!field || !field.configurationJson) {
      return {};
    }

    if (typeof field.configurationJson === 'object') {
      return field.configurationJson as Record<string, any>;
    }

    try {
      return JSON.parse(field.configurationJson) as Record<string, any>;
    } catch (error) {
      console.warn('Failed to parse field configuration JSON:', error, field?.code);
      return {};
    }
  }

  /**
   * Loads an image from URL and returns it as ImageData
   */
  private static loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  /**
   * Loads an image from base64 data and returns it as ImageData
   */
  private static loadImageFromBase64(base64: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      // Ensure base64 data includes data URL prefix if not already present
      img.src = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
    });
  }

  /**
   * Generates a truly interactive PDF with editable form fields using pdf-lib
   * Returns the PDF as a Uint8Array so the caller can decide whether to download or display it
   * 
   * When options.pixelVersion is true, the system uses enhanced configuration from:
   * - FormFields.ConfigurationJson (parsed via parseFieldConfiguration)
   * - FormSections.ConfigurationJson (used for layout and positioning)
   * This enables advanced field customization including fontSize, textAlign, fieldOnNextLine,
   * displayAsLabel, multilineLines, checkboxColumns, and more.
   */
  static async generateEditablePDF(
    formData: any,
    formTitle: string,
    formSections: any[],
    options: PDFGenerationOptions = {}
  ): Promise<Uint8Array> {
    const {
      filename = 'form.pdf',
      headerConfigurationJson,
      headerHeight = 100,
      pageMargin = 15,
      headerBottomMargin = 20,
      headerData = {},
      // Form layout configuration
      numberOfColumns = 2,
      // Font size configuration with defaults
      defaultFontSize = 10,
      labelFontSize = 8.5,
      multilineFontSize = 10,
      sectionTitleFontSize = 12,
      // Padding and spacing configuration with defaults
      fieldSpacing = 3.5,
      sectionSpacing = 12,
      checkboxListSpacing = -7,
      checkboxListColumns = 3,
      sectionPadding = 3,
      fieldHeight: configFieldHeight = 16
    } = options;

    const showSectionBorders = options.showSectionBorders ?? true;
    
    console.log('PDF config (generateEditablePDF):', {
      fieldSpacing,
      sectionSpacing,
      checkboxListSpacing,
      sectionPadding,
      fieldHeight: configFieldHeight,
      showSectionBorders
    });

    try {
      // Create a new PDF document
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([595, 842]); // A4 size in points
      const { width: pageWidth, height: pageHeight } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const form = pdfDoc.getForm();

      // Ensure headerData contains user data fields if not already populated
      if (headerConfigurationJson && (!headerData || Object.keys(headerData).length <= 1)) {
        try {
          if (options.userDataFields && options.userDataFields.length > 0) {
            console.log('[PDFService] Using provided UserDataFields from options for header injection...');
            options.userDataFields.forEach((field: any) => {
              if (field.businessDataField?.code) {
                headerData[field.businessDataField.code] = field.value;
              }
            });
            console.log(`[PDFService] Injected ${options.userDataFields.length} user fields into headerData from options`);
          } else {
            console.log('[PDFService] Fetching UserDataFields for header injection...');
            const userDataFields = await customUserDataFieldService.getCurrentUserFields();
            if (userDataFields && userDataFields.length > 0) {
              userDataFields.forEach(field => {
                if (field.businessDataField?.code) {
                  headerData[field.businessDataField.code] = field.value;
                }
              });
              console.log(`[PDFService] Injected ${userDataFields.length} user fields into headerData`);

              // Add to options for downstream use (e.g. addAbsoluteFieldToPDF)
              options.userDataFields = userDataFields;
            }
          }
        } catch (err) {
          console.warn('[PDFService] Failed to fetch/process UserDataFields for header:', err);
        }
      }

      // Add header - prefer dynamic header configuration, fallback to image
      let headerImgHeight = 0;
      if (headerConfigurationJson) {
        console.log('PDF: headerConfigurationJson found:', headerConfigurationJson);
        try {
          const headerConfig = JSON.parse(headerConfigurationJson) as HeaderConfiguration;
          console.log('PDF: Parsed headerConfig:', headerConfig);
          console.log('PDF: headerConfig.type:', headerConfig.type);
          console.log('PDF: headerConfig.elements:', headerConfig.elements);
          if (headerConfig.type === 'dynamic' && headerConfig.elements) {
            console.log('PDF: Rendering dynamic header');
            headerImgHeight = await this.renderDynamicHeader(
              page,
              pdfDoc,
              headerConfig,
              pageWidth,
              pageHeight,
              pageMargin,
              headerHeight || headerConfig.height || 100,
              font,
              boldFont,
              headerData
            );
            console.log('PDF: Dynamic header rendered, height:', headerImgHeight);
          } else {
            // If type is 'image' or invalid, fall through to image rendering
            console.log('HeaderConfiguration type is not dynamic, falling back to image. type:', headerConfig.type, 'has elements:', !!headerConfig.elements);
          }
        } catch (error) {
          console.warn('Could not parse HeaderConfigurationJson, falling back to image:', error);
          // Fall through to image rendering
        }
      } else {
        console.log('PDF: No headerConfigurationJson provided');
      }
      
      // No header image functionality - removed

      let currentPage = page;
      let currentY = pageHeight - headerImgHeight - headerBottomMargin;
      const fieldHeight = configFieldHeight; // Use configured field height
      const contentWidth = pageWidth - (pageMargin * 2); // Full width content to match headers
      
      // Log formData to see what values are available
      console.log('PDF formData received:', formData);
      console.log('PDF formData keys:', Object.keys(formData || {}));
      console.log('PDF formData CheckboxList fields:', Object.keys(formData || {}).filter(key => {
        // Find fields that are CheckboxList type
        return formSections.some((s: any) => 
          s.formFields?.some((f: any) => f.code === key && this.normalizeFieldType(f.fieldType) === 'CheckboxList')
        );
      }));
      
      console.log(`PDF Layout - pageHeight: ${pageHeight}, headerImgHeight: ${headerImgHeight}, pageMargin: ${pageMargin}, headerBottomMargin: ${headerBottomMargin}`);
      console.log(`PDF Layout - Starting currentY: ${currentY} (calculated as: ${pageHeight} - ${headerImgHeight} - ${headerBottomMargin})`);
      console.log(`PDF Layout - Header bottom edge: ${pageHeight - headerImgHeight}, Content start: ${currentY}, Gap: ${(pageHeight - headerImgHeight) - currentY}`);

      // Log what sections are being received with more detail for CheckboxList fields
      console.log('PDF received formSections:', formSections.map((s: any) => ({
        code: s.code,
        description: s.description,
        hasFormFields: !!s.formFields,
        formFieldsCount: s.formFields?.length || 0,
        formFields: s.formFields?.map((f: any) => ({
          code: f.code,
          fieldType: f.fieldType,
          hasFormFieldDataType: !!f.formFieldDataType,
          formFieldDataItemsCount: f.formFieldDataType?.formFieldDataItems?.length || 0
        })) || []
      })));

      // Sort sections by display order
      const sortedSections = formSections
        .filter((section: any) => section.isActive)
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      // Group sections by their position in the layout (column groups)
      if (options.pixelVersion) {
        console.log('PDF: Using PixelVersion renderer', sortedSections, pageMargin);
        await this.renderPixelVersion(
          sortedSections,
          pdfDoc,
          page, // First page already created
          headerConfigurationJson,
          headerHeight,
          pageMargin,
          headerData,
          font,
          boldFont,
          form,
          formData,
          pageWidth,
          pageHeight,
          defaultFontSize,
          labelFontSize,
          options
        );

        const pdfBytes = await pdfDoc.save();
        return pdfBytes;
      }

      let i = 0;
      while (i < sortedSections.length) {
        const currentSection = sortedSections[i];

        console.log(`Starting section ${i} (${currentSection.description}) - currentY: ${currentY}`);

        // Check if this is a full-width section (columnNumber === 0)
        if (currentSection.columnNumber === 0) {
          // Render full-width section
          const result = await this.renderFullWidthSection(
            currentSection,
            currentPage,
            pdfDoc,
            currentY,
            pageMargin,
            contentWidth,
            fieldHeight,
            fieldSpacing,
            checkboxListSpacing,
            sectionSpacing,
            sectionPadding,
            font,
            boldFont,
            form,
            formData,
            pageWidth,
            pageHeight,
            headerImgHeight,
            headerConfigurationJson,
            headerHeight,
            headerBottomMargin,
            numberOfColumns,
            defaultFontSize,
            labelFontSize,
            multilineFontSize,
            sectionTitleFontSize,
            headerData,
            showSectionBorders,
            checkboxListColumns
          );
          currentPage = result.page;
          currentY = result.y;

          console.log(`After full-width section ${i} - result.y: ${result.y}, currentY: ${currentY}`);
          
          // Add spacing after this section (except for the last section)
          if (i < sortedSections.length - 1) {
            currentY -= sectionSpacing; // Subtract in PDF coordinates (Y=0 at bottom)
            console.log(`Added spacing ${sectionSpacing} - new currentY: ${currentY}`);
          }
          
          i++;
        } else {
          // Find all consecutive sections that are in columns
          const columnSections: any[] = [];
          let j = i;
          while (j < sortedSections.length && sortedSections[j].columnNumber !== 0) {
            columnSections.push(sortedSections[j]);
            j++;
          }
          
          // Render column sections side by side
          const result = await this.renderColumnSections(
            columnSections,
            currentPage,
            pdfDoc,
            currentY,
            pageMargin,
            contentWidth,
            fieldHeight,
            fieldSpacing,
            checkboxListSpacing,
            sectionSpacing,
            sectionPadding,
            font,
            boldFont,
            form,
            formData,
            pageWidth,
            pageHeight,
            headerImgHeight,
            headerConfigurationJson,
            headerHeight,
            headerBottomMargin,
            numberOfColumns,
            defaultFontSize,
            labelFontSize,
            multilineFontSize,
            sectionTitleFontSize,
            headerData,
            showSectionBorders,
            checkboxListColumns
          );
          currentPage = result.page;
          currentY = result.y;

          console.log(`After column sections ${i}-${j-1} - result.y: ${result.y}, currentY: ${currentY}`);
          
          // Add spacing after these column sections (except for the last sections)
          if (j < sortedSections.length) {
            currentY -= sectionSpacing; // Subtract in PDF coordinates (Y=0 at bottom)
            console.log(`Added spacing ${sectionSpacing} - new currentY: ${currentY}`);
          }
          
          i = j;
        }
      }

      // Save and return the PDF bytes
      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
    } catch (error) {
      console.error('Error generating editable PDF:', error);
      throw new Error('Failed to generate editable PDF');
    }
  }

  /**
   * Renders a full-width section
   */
  private static async renderFullWidthSection(
    section: any,
    currentPage: PDFPage,
    pdfDoc: PDFDocument,
    startY: number,
    pageMargin: number,
    contentWidth: number,
    fieldHeight: number,
    fieldSpacing: number,
    checkboxListSpacing: number,
    sectionSpacing: number,
    sectionPadding: number,
    font: PDFFont,
    boldFont: PDFFont,
    form: PDFForm,
    formData: any,
    pageWidth: number,
    pageHeight: number,
    headerImgHeight: number,
    headerConfigurationJson: string | undefined,
    headerHeight: number,
    headerBottomMargin: number,
    numberOfColumns: number,
    defaultFontSize: number,
    labelFontSize: number,
    multilineFontSize: number,
    sectionTitleFontSize: number,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {},
    showSectionBorders: boolean = true,
    checkboxListColumns: number = 3
  ): Promise<{ page: PDFPage, y: number }> {
    let page = currentPage;
    let currentY = startY;
    
    console.log(`renderFullWidthSection - startY: ${startY}, currentY: ${currentY}`);

    // Check if we need a new page
    if (currentY - 25 < pageMargin) {
      page = pdfDoc.addPage([595, 842]);
      const newHeaderHeight = await this.addHeaderToPage(
        page,
        pdfDoc,
        headerConfigurationJson,
        pageWidth,
        pageHeight,
        pageMargin,
        headerHeight,
        font,
        boldFont,
        headerData
      );
      currentY = pageHeight - (newHeaderHeight || headerImgHeight) - headerBottomMargin;
    }

    // Get fields for this section
    const sectionFields = section.formFields
      ?.filter((field: any) => field.isActive)
      ?.sort((a: any, b: any) => a.displayOrder - b.displayOrder) || [];
    
    console.log(`PDF Section "${section.description}" has ${sectionFields.length} active fields:`, 
      sectionFields.map((f: any) => `${f.code} (${f.fieldType})`));
    
    const sectionStartY = currentY;
    
    // Group fields into rows based on displayOrder and columnNumber
    const fieldRows: any[][] = [];
    let currentRow: any[] = [];
    let lastFieldColumn = 0;
    
    for (let i = 0; i < sectionFields.length; i++) {
      const field = sectionFields[i];
      // Check if field has fieldOnNextLine - if so, treat as full-width
      const fieldConfig = this.parseFieldConfiguration(field);
      const hasFieldOnNextLine = fieldConfig?.fieldOnNextLine || false;
      const fieldColumn = hasFieldOnNextLine ? 0 : (field.columnNumber || 0);
      
      // If this field has columnNumber > 0, it's a column field
      if (fieldColumn > 0) {
        // Check if we should start a new row
        if (currentRow.length > 0 && fieldColumn <= lastFieldColumn) {
          // Start a new row
          fieldRows.push(currentRow);
          currentRow = [field];
        } else {
          // Add to current row
          currentRow.push(field);
        }
        lastFieldColumn = fieldColumn;
      } else {
        // Full-width field - finish current row and add this as a separate row
        if (currentRow.length > 0) {
          fieldRows.push(currentRow);
          currentRow = [];
        }
        fieldRows.push([field]); // Full-width field is its own row
        lastFieldColumn = 0;
      }
    }
    
    // Don't forget the last row
    if (currentRow.length > 0) {
      fieldRows.push(currentRow);
    }
    
    // Calculate consistent label width for ALL fields in this section for alignment
    const availableWidth = contentWidth - (sectionPadding * 2);
    const sectionMaxLabelWidth = this.calculateMaxLabelWidth(sectionFields, availableWidth, font);
    
    // Calculate total height based on rows (each row has one height, not per-field, accounting for wrapped labels)
    let totalRowHeight = 0;
    for (let rowIdx = 0; rowIdx < fieldRows.length; rowIdx++) {
      const rowFields = fieldRows[rowIdx];
      const hasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
      if (hasBlackEdgeTape) {
        console.log(`🔵🔵🔵 BLACK_EDGE_TAPE ROW ${rowIdx}: fields in this row:`, rowFields.map((f: any) => f.code));
      }
      let rowHeight = 0;
      let checkboxListExtraLabelHeight = 0; // Track extraLabelHeight for checkbox lists in this row
      for (const field of rowFields) {
        const fieldType = this.normalizeFieldType(field.fieldType);
        const isMultiline = fieldType === 'Multiline';
        const isCheckbox = fieldType === 'Bool';
        const isCheckboxList = fieldType === 'CheckboxList';
        
        // Calculate label height to check if it wraps (only add extra height if wrapping)
        const labelHeight = this.calculateLabelHeight(field, sectionMaxLabelWidth - 5, font);
        const baseLabelHeight = 8; // Base label font size
        // Only add extra height if label wraps beyond single line
        const extraLabelHeight = Math.max(0, labelHeight - baseLabelHeight);
        
        // Track extraLabelHeight for checkbox lists (will be added to spacing below, not height)
        if (isCheckboxList && extraLabelHeight > 0) {
          checkboxListExtraLabelHeight = Math.max(checkboxListExtraLabelHeight, extraLabelHeight);
        }
        
        const fieldConfig = this.parseFieldConfiguration(field);
        const fieldOnNextLine = fieldConfig.fieldOnNextLine || false;
        const isHorizontalRule = fieldType === 'HorizontalRule';
        let effectiveHeight = fieldHeight;
        if (isMultiline) {
          const configuredMultilineFontSize =
            fieldConfig.fontSize ?? multilineFontSize ?? PDFService.DEFAULT_MULTILINE_FONT_SIZE;
          const configuredMultilineLines =
            fieldConfig.multilineLines ?? PDFService.MULTILINE_LINES;
          effectiveHeight = this.calculateCustomMultilineHeight(
            configuredMultilineFontSize,
            configuredMultilineLines
          );
          // If field is on next line, add label height + spacing
          if (fieldOnNextLine && field.description) {
            effectiveHeight += baseLabelHeight + 2; // baseLabelHeight (8pt) + spacing (2pt)
          }
        } else if (isCheckbox) {
          // Use fieldHeight (16) as base to account for label space, same as other fields
          // The checkbox itself is 10pt, but the field takes up fieldHeight including label
          effectiveHeight = fieldHeight;
        } else if (isCheckboxList) {
          // Calculate height needed for checkbox list using dynamic row heights
          // This matches the rendering approach - calculate actual height of each row
          const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
          const fieldNumColumns = fieldConfig.checkboxColumns ?? checkboxListColumns;
          const numColumns = fieldNumColumns;
          const numRows = Math.ceil(items.length / numColumns);
          const checkboxSize = 10; // Matches regular checkboxes
          const labelFontSize = 8; // Match rendering labelFontSize
          const lineSpacing = 2; // Spacing between wrapped lines
          const checkboxLabelSpacing = 2; // Space between checkbox and its label text

          console.log(`🔵 CHECKBOX LIST HEIGHT CALC: ${field.code} (${field.description})`);
          if (field.code === 'BLACK_EDGE_TAPE') {
            console.log(`🔵🔵🔵 BLACK_EDGE_TAPE HEIGHT CALCULATION START 🔵🔵🔵`);
          }
          if (field.code === 'SINK_TYPE') {
            console.log(`🔵🔵🔵 SINK_TYPE HEIGHT CALCULATION START 🔵🔵🔵`);
          }
          if (field.code === 'DOOR_TYPE') {
            console.log(`🔵🔵🔵 DOOR_TYPE HEIGHT CALCULATION START 🔵🔵🔵`);
          }
          if (field.code === 'HANDLE_TYPE') {
            console.log(`🔵🔵🔵 HANDLE_TYPE HEIGHT CALCULATION START 🔵🔵🔵`);
          }
          console.log(`🔵   items.length: ${items.length}, numRows: ${numRows}, numColumns: ${numColumns}`);
          
          // Calculate column width for wrapping calculation
          // For checkbox lists, use the actual field width (availableWidth minus label area and gap)
          // If field has a label, reserve space for it; otherwise use full width
          const fieldHasLabel = field.description && field.description.trim().length > 0;
          const labelFieldGap = 3; // Same as in rendering
          const columnGap = 10; // Horizontal gap between columns
          const actualFieldWidth = fieldHasLabel 
            ? availableWidth - sectionMaxLabelWidth - labelFieldGap
            : availableWidth;
          const columnWidth = (actualFieldWidth - (columnGap * (numColumns - 1))) / numColumns;
          const maxLabelWidth = columnWidth - checkboxSize - checkboxLabelSpacing - 1; // checkboxSize + checkboxLabelSpacing + padding
          
          console.log(`🔵   availableWidth: ${availableWidth}, sectionMaxLabelWidth: ${sectionMaxLabelWidth}, fieldHasLabel: ${fieldHasLabel}`);
          console.log(`🔵   actualFieldWidth: ${actualFieldWidth}, columnWidth: ${columnWidth}, maxLabelWidth: ${maxLabelWidth}`);
          
          // Calculate height for each row based on tallest label in that row
          const rowHeights: number[] = [];
          
          for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
            let maxRowHeight = checkboxSize; // Base height is checkbox size
            
            // Check all items in this row (items in different columns but same row)
            for (let colIndex = 0; colIndex < numColumns; colIndex++) {
              const itemIndex = rowIndex * numColumns + colIndex;
              if (itemIndex >= items.length) break;
              
              const item = items[itemIndex];
              const labelText = item.description || '';
              
              // Calculate how many lines this label needs
              const numLines = this.calculateCheckboxListItemLines(labelText, maxLabelWidth, font, labelFontSize);
              
              console.log(`🔵   item ${itemIndex} (row ${rowIndex}, col ${colIndex}): "${labelText}" -> ${numLines} lines`);
              
              // Calculate height needed for this label (accounting for wrapped lines)
              const labelHeight = numLines === 1 
                ? labelFontSize // Single line
                : labelFontSize + ((numLines - 1) * (labelFontSize + lineSpacing)); // Wrapped lines
              
              // Row height is the maximum of checkbox height and label height
              const itemHeight = Math.max(checkboxSize, labelHeight);
              maxRowHeight = Math.max(maxRowHeight, itemHeight);
            }
            
            // Add minimum spacing between rows (except for the last row, which doesn't need spacing after it)
            // The spacing will be handled by the field spacing below the checkbox list
            const isLastRow = rowIndex === numRows - 1;
            rowHeights.push(maxRowHeight + (isLastRow ? 0 : PDFService.CHECKBOX_LIST_ROW_SPACING));
            
            console.log(`🔵   row ${rowIndex}: maxRowHeight=${maxRowHeight}, isLastRow=${isLastRow}, rowHeight=${rowHeights[rowIndex]}`);
            if (field.code === 'DOOR_TYPE') {
              console.log(`🔵🔵🔵 DOOR_TYPE row ${rowIndex}: maxRowHeight=${maxRowHeight}, isLastRow=${isLastRow}, rowHeight=${rowHeights[rowIndex]}`);
            }
            if (field.code === 'HANDLE_TYPE') {
              console.log(`🔵🔵🔵 HANDLE_TYPE row ${rowIndex}: maxRowHeight=${maxRowHeight}, isLastRow=${isLastRow}, rowHeight=${rowHeights[rowIndex]}`);
            }
          }
          
          // Sum up all row heights
          effectiveHeight = rowHeights.reduce((sum, height) => sum + height, 0);
          
          if (field.code === 'DOOR_TYPE') {
            console.log(`🔵🔵🔵 DOOR_TYPE: rowHeights=`, rowHeights, `effectiveHeight=${effectiveHeight}`);
          }
          if (field.code === 'HANDLE_TYPE') {
            console.log(`🔵🔵🔵 HANDLE_TYPE: rowHeights=`, rowHeights, `effectiveHeight=${effectiveHeight}`);
          }
          
          console.log(`🔵   rowHeights:`, rowHeights);
          console.log(`🔵   effectiveHeight: ${effectiveHeight} (sum of all row heights)`);
        }
        
        // Only add extra height if label wraps (maintains original spacing for single-line labels)
        // For checkbox lists, the checkbox items are positioned below the label with a 2pt gap
        // The label baseline is at y - height + 12, and checkboxes start at y - height + 10
        // So checkboxes start at (height - 10)pt below the field top
        // The last checkbox's top is at y - height + 10 - cumulativeHeight
        // The last checkbox's bottom is at (y - height + 10 - cumulativeHeight) - checkboxSize = y - height - cumulativeHeight
        // So the total height from field top to bottom of last checkbox is height + cumulativeHeight
        if (isCheckboxList) {
          // For checkbox lists, the checkbox items start at y - height + 10
          // The sum of row heights already includes the spacing between rows
          // We need to add:
          // 1. The distance from the field top to where checkboxes start: (height - 10)
          // 2. The checkbox size itself (10pt) to account for the checkbox extending below its Y position
          const checkboxSize = 10; // Size of checkbox
          const checkboxStartOffset = fieldHeight - 10; // Distance from field top to checkbox start (for height=16, this is 6pt)
          effectiveHeight = effectiveHeight + checkboxStartOffset + checkboxSize;
          console.log(`🔵   ${field.code} checkbox list: effectiveHeight=${effectiveHeight} (sum of row heights + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
          if (field.code === 'DOOR_TYPE' || field.code === 'HANDLE_TYPE') {
            console.log(`🔵🔵🔵 ${field.code} CALC: effectiveHeight=${effectiveHeight} (sum of row heights=${effectiveHeight - checkboxStartOffset - checkboxSize} + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
          }
        } else {
          effectiveHeight = effectiveHeight + extraLabelHeight;
          if (field.code === 'SINK_UNDERMOUNT') {
            console.log(`🔵🔵🔵 SINK_UNDERMOUNT CALC: extraLabelHeight=${extraLabelHeight}, effectiveHeight=${effectiveHeight}`);
          }
        }
        
        rowHeight = Math.max(rowHeight, effectiveHeight);
        
        if (isCheckboxList) {
          console.log(`🔵   rowHeight: ${rowHeight} (max of ${effectiveHeight})`);
          if (field.code === 'SINK_TYPE') {
            console.log(`🔵🔵🔵 SINK_TYPE CALC: final rowHeight=${rowHeight}`);
          }
        }
        if (field.code === 'SINK_UNDERMOUNT') {
          console.log(`🔵🔵🔵 SINK_UNDERMOUNT CALC: final rowHeight=${rowHeight}`);
        }
      }
      
      const calcHasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
      const calcHasCheckboxList = rowFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
      const calcHasSinkType = rowFields.some((f: any) => f.code === 'SINK_TYPE');
      const calcHasSinkUndermount = rowFields.some((f: any) => f.code === 'SINK_UNDERMOUNT');
      
      // Check if next row has a horizontal rule (to reduce spacing before it)
      const calcNextRowHasHorizontalRule = rowIdx < fieldRows.length - 1 && 
        fieldRows[rowIdx + 1].some((f: any) => this.normalizeFieldType(f.fieldType) === 'HorizontalRule');
      
      // Check for spacingAfter configuration on any field in this row
      let calcSpacingAfter = 0;
      for (const field of rowFields) {
        const fieldConfig = this.parseFieldConfiguration(field);
        const spacingAfter = fieldConfig.spacingAfter || 0;
        if (calcHasSinkType || calcHasSinkUndermount) {
          console.log(`🔵 SINK CALC: field=${field.code}, spacingAfter=${spacingAfter}, config=`, fieldConfig);
        }
        calcSpacingAfter = Math.max(calcSpacingAfter, spacingAfter);
      }
      
      // Add extra spacing for checkbox lists
      // Note: Don't add extraLabelHeight to spacing below - the wrapped label already takes up space naturally
      // The checkbox list height doesn't include extraLabelHeight, so spacing is consistent
      // Reduce spacing before horizontal rules (use quarter of fieldSpacing)
      const calcBaseSpacing = calcNextRowHasHorizontalRule ? fieldSpacing / 4 : fieldSpacing;
      const calcExtraSpacing = calcHasCheckboxList ? checkboxListSpacing : 0;
      const calcTotalSpacing = calcBaseSpacing + calcExtraSpacing + calcSpacingAfter;
      
      if (calcHasSinkType || calcHasSinkUndermount) {
        console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx}: rowFields=`, rowFields.map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)}, col=${f.columnNumber || 0})`));
        console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx}: calcHasCheckboxList=${calcHasCheckboxList}, calcHasSinkType=${calcHasSinkType}, calcHasSinkUndermount=${calcHasSinkUndermount}`);
        console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx}: rowHeight=${rowHeight}, fieldSpacing=${fieldSpacing}, checkboxListSpacing=${checkboxListSpacing}, calcExtraSpacing=${calcExtraSpacing}, calcSpacingAfter=${calcSpacingAfter}, calcTotalSpacing=${calcTotalSpacing}`);
        console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx}: totalRowHeight before adding=${totalRowHeight}, adding=${rowHeight + calcTotalSpacing}`);
      }
      
      if (calcHasBlackEdgeTape || calcHasCheckboxList) {
        console.log(`🔵   ROW ${rowIdx}: fieldSpacing: ${fieldSpacing}, extraSpacing: ${calcExtraSpacing}, totalSpacing: ${calcTotalSpacing}, rowHeight before spacing: ${rowHeight}`);
        console.log(`🔵   ROW ${rowIdx}: totalRowHeight before adding: ${totalRowHeight}, adding ${rowHeight + calcTotalSpacing}`);
      }
      
      totalRowHeight += rowHeight + calcTotalSpacing;
      
      if (calcHasBlackEdgeTape || calcHasCheckboxList || calcHasSinkType || calcHasSinkUndermount) {
        console.log(`🔵   ROW ${rowIdx}: totalRowHeight after adding: ${totalRowHeight}`);
        if (calcHasBlackEdgeTape) {
          console.log(`🔵🔵🔵 BLACK_EDGE_TAPE ROW ${rowIdx} COMPLETE: rowHeight=${rowHeight}, totalSpacing=${calcTotalSpacing}, totalRowHeight=${totalRowHeight}`);
          if (rowIdx < fieldRows.length - 1) {
            console.log(`🔵🔵🔵 NEXT ROW (${rowIdx + 1}) fields:`, fieldRows[rowIdx + 1].map((f: any) => f.code));
          } else {
            console.log(`🔵🔵🔵 BLACK_EDGE_TAPE is in LAST ROW of section`);
          }
        }
        if (calcHasSinkType || calcHasSinkUndermount) {
          console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx} COMPLETE: rowHeight=${rowHeight}, totalSpacing=${calcTotalSpacing}, totalRowHeight=${totalRowHeight}`);
          if (rowIdx < fieldRows.length - 1) {
            console.log(`🔵🔵🔵 SINK CALC NEXT ROW (${rowIdx + 1}) fields:`, fieldRows[rowIdx + 1].map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)}, col=${f.columnNumber || 0})`));
          } else {
            console.log(`🔵🔵🔵 SINK CALC ROW ${rowIdx} is LAST ROW of section`);
          }
        }
      }
    }
    
    // Calculate section height to draw border
    const sectionHeight = totalRowHeight + 15;
    
    // Log section height if it contains checkbox lists or BLACK_EDGE_TAPE
    const hasCheckboxList = sectionFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
    const hasBlackEdgeTape = sectionFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
    if (hasCheckboxList || hasBlackEdgeTape) {
      console.log(`🔵 SECTION "${section.description}": totalRowHeight: ${totalRowHeight}, sectionHeight: ${sectionHeight} (totalRowHeight + 15)`);
      if (hasBlackEdgeTape) {
        console.log(`🔵🔵🔵 BLACK_EDGE_TAPE SECTION HEIGHT: totalRowHeight=${totalRowHeight}, sectionHeight=${sectionHeight} (includes +15 padding)`);
      }
    }
    
    // Draw section border (gray rectangle) - only if showSectionBorders is true
    if (showSectionBorders) {
      page.drawRectangle({
        x: pageMargin,
        y: currentY - sectionHeight,
        width: contentWidth,
        height: sectionHeight,
        borderColor: rgb(0.7, 0.7, 0.7),
        borderWidth: 1,
      });
    }

    // Draw white background for section title (only if description exists and borders are shown)
    // Check for both null/undefined and empty string
    const hasDescription = !!(section.description && section.description.trim().length > 0);
    if (hasDescription && showSectionBorders) {
      const titleWidth = boldFont.widthOfTextAtSize(section.description, sectionTitleFontSize) + 10;
      page.drawRectangle({
        x: pageMargin + 10,
        y: currentY - 8,
        width: titleWidth,
        height: 1,
        color: rgb(1, 1, 1),
        borderWidth: 0,
      });

      // Add section header on top of border
      page.drawText(section.description, {
        x: pageMargin + 15,
        y: currentY - 4,
        size: sectionTitleFontSize,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
    }
    // Reduce gap before first field if section has no description
    // hasDescription is already defined above
    console.log(`🔵🔵🔵 FULL-WIDTH RENDER SECTION "${section.description}": hasDescription: ${String(hasDescription)}, currentY before adjustment: ${currentY}`);
    currentY -= hasDescription ? 20 : 10;
    console.log(`🔵🔵🔵 FULL-WIDTH RENDER SECTION "${section.description}": currentY after adjustment: ${currentY} (reduced by ${hasDescription ? 20 : 5})`);

    // Render all fields in this section with padding
    let sectionContinues = false;
    let continuedSectionStartY = 0;
    
    // Render each row
    for (let rowIndex = 0; rowIndex < fieldRows.length; rowIndex++) {
      const rowFields = fieldRows[rowIndex];
      
      // Calculate row height based on tallest field in this row (accounting for wrapped labels)
      let rowHeight = 0;
      let checkboxListExtraLabelHeight = 0; // Track extraLabelHeight for checkbox lists in this row
      for (const field of rowFields) {
        const fieldType = this.normalizeFieldType(field.fieldType);
        const isMultiline = fieldType === 'Multiline';
        const isCheckbox = fieldType === 'Bool';
        const isCheckboxList = fieldType === 'CheckboxList';
        
        // Calculate label height to check if it wraps (only add extra height if wrapping)
        const labelHeight = this.calculateLabelHeight(field, sectionMaxLabelWidth - 5, font);
        const baseLabelHeight = 8; // Base label font size
        // Only add extra height if label wraps beyond single line
        const extraLabelHeight = Math.max(0, labelHeight - baseLabelHeight);
        
        // Track extraLabelHeight for checkbox lists (will be added to spacing below, not height)
        if (isCheckboxList && extraLabelHeight > 0) {
          checkboxListExtraLabelHeight = Math.max(checkboxListExtraLabelHeight, extraLabelHeight);
        }
        
        const fieldConfig = this.parseFieldConfiguration(field);
        let effectiveHeight = fieldHeight;
        if (isMultiline) {
          const configuredMultilineFontSize =
            fieldConfig.fontSize ?? multilineFontSize ?? PDFService.DEFAULT_MULTILINE_FONT_SIZE;
          const configuredMultilineLines =
            fieldConfig.multilineLines ?? PDFService.MULTILINE_LINES;
          effectiveHeight = this.calculateCustomMultilineHeight(
            configuredMultilineFontSize,
            configuredMultilineLines
          );
        } else if (isCheckbox) {
          // Use fieldHeight (16) as base to account for label space, same as other fields
          // The checkbox itself is 10pt, but the field takes up fieldHeight including label
          effectiveHeight = fieldHeight;
        } else if (isCheckboxList) {
          // Calculate height needed for checkbox list using dynamic row heights
          // This matches the rendering approach - calculate actual height of each row
          const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
          const fieldNumColumns = fieldConfig.checkboxColumns ?? checkboxListColumns;
          const numColumns = fieldNumColumns;
          const numRows = Math.ceil(items.length / numColumns);
          const checkboxSize = 10; // Matches regular checkboxes
          const labelFontSize = 8; // Match rendering labelFontSize
          const lineSpacing = 2; // Spacing between wrapped lines
          const checkboxLabelSpacing = 2; // Space between checkbox and its label text

          console.log(`🔵 RENDER CHECKBOX LIST HEIGHT CALC: ${field.code} (${field.description})`);
          if (field.code === 'SINK_TYPE') {
            console.log(`🔵🔵🔵 SINK_TYPE RENDER HEIGHT CALCULATION START 🔵🔵🔵`);
          }
          console.log(`🔵   items.length: ${items.length}, numRows: ${numRows}`);
          
          // Calculate column width for wrapping calculation
          // Use the same calculation as the calculation phase for consistency
          // Calculate availableWidth (same as in calculation phase)
          const calcAvailableWidth = contentWidth - (sectionPadding * 2);
          const fieldHasLabel = field.description && field.description.trim().length > 0;
          const labelFieldGap = 3; // Same as in calculation phase
          const columnGap = 10; // Horizontal gap between columns
          const actualFieldWidth = fieldHasLabel 
            ? calcAvailableWidth - sectionMaxLabelWidth - labelFieldGap
            : calcAvailableWidth;
          const columnWidth = (actualFieldWidth - (columnGap * (numColumns - 1))) / numColumns;
          const maxLabelWidth = columnWidth - checkboxSize - checkboxLabelSpacing - 1; // checkboxSize + checkboxLabelSpacing + padding
          
          if (field.code === 'SINK_TYPE') {
            console.log(`🔵🔵🔵 SINK_TYPE RENDER: calcAvailableWidth=${calcAvailableWidth}, sectionMaxLabelWidth=${sectionMaxLabelWidth}, fieldHasLabel=${fieldHasLabel}`);
            console.log(`🔵🔵🔵 SINK_TYPE RENDER: actualFieldWidth=${actualFieldWidth}, columnWidth=${columnWidth}, maxLabelWidth=${maxLabelWidth}`);
          }
          
          // Calculate height for each row based on tallest label in that row
          const rowHeights: number[] = [];
          
          for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
            let maxRowHeight = checkboxSize; // Base height is checkbox size
            
            // Check all items in this row (items in different columns but same row)
            for (let colIndex = 0; colIndex < numColumns; colIndex++) {
              const itemIndex = rowIndex * numColumns + colIndex;
              if (itemIndex >= items.length) break;
              
              const item = items[itemIndex];
              const labelText = item.description || '';
              
              // Calculate how many lines this label needs
              const numLines = this.calculateCheckboxListItemLines(labelText, maxLabelWidth, font, labelFontSize);
              
              // Calculate height needed for this label (accounting for wrapped lines)
              const labelHeight = numLines === 1 
                ? labelFontSize // Single line
                : labelFontSize + ((numLines - 1) * (labelFontSize + lineSpacing)); // Wrapped lines
              
              // Row height is the maximum of checkbox height and label height
              const itemHeight = Math.max(checkboxSize, labelHeight);
              maxRowHeight = Math.max(maxRowHeight, itemHeight);
            }
            
            // Add minimum spacing between rows (except for the last row, which doesn't need spacing after it)
            // The spacing will be handled by the field spacing below the checkbox list
            const isLastRow = rowIndex === numRows - 1;
            rowHeights.push(maxRowHeight + (isLastRow ? 0 : PDFService.CHECKBOX_LIST_ROW_SPACING));
            
            console.log(`🔵   RENDER row ${rowIndex}: maxRowHeight=${maxRowHeight}, rowHeight=${rowHeights[rowIndex]}`);
          }
          
          // Sum up all row heights
          effectiveHeight = rowHeights.reduce((sum, height) => sum + height, 0);
          
          console.log(`🔵   RENDER: rowHeights:`, rowHeights);
          console.log(`🔵   RENDER: effectiveHeight: ${effectiveHeight} (sum of all row heights)`);
        }
        
        // Only add extra height if label wraps (maintains original spacing for single-line labels)
        // For checkbox lists, the checkbox items are positioned below the label with a 2pt gap
        // The label baseline is at y - height + 12, and checkboxes start at y - height + 10
        // So checkboxes start at (height - 10)pt below the field top
        // The last checkbox's top is at y - height + 10 - cumulativeHeight
        // The last checkbox's bottom is at (y - height + 10 - cumulativeHeight) - checkboxSize = y - height - cumulativeHeight
        // So the total height from field top to bottom of last checkbox is height + cumulativeHeight
        if (isCheckboxList) {
          // For checkbox lists, the checkbox items start at y - height + 10
          // The sum of row heights already includes the spacing between rows
          // We need to add:
          // 1. The distance from the field top to where checkboxes start: (height - 10)
          // 2. The checkbox size itself (10pt) to account for the checkbox extending below its Y position
          const checkboxSize = 10; // Size of checkbox
          const checkboxStartOffset = fieldHeight - 10; // Distance from field top to checkbox start (for height=16, this is 6pt)
          effectiveHeight = effectiveHeight + checkboxStartOffset + checkboxSize;
          console.log(`🔵   RENDER: ${field.code} checkbox list: effectiveHeight=${effectiveHeight} (sum of row heights + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
          if (field.code === 'DOOR_TYPE' || field.code === 'HANDLE_TYPE') {
            console.log(`🔵🔵🔵 ${field.code} RENDER: effectiveHeight=${effectiveHeight} (sum of row heights=${effectiveHeight - checkboxStartOffset - checkboxSize} + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
          }
        } else {
          effectiveHeight = effectiveHeight + extraLabelHeight;
          if (field.code === 'SINK_UNDERMOUNT') {
            console.log(`🔵🔵🔵 SINK_UNDERMOUNT RENDER: extraLabelHeight=${extraLabelHeight}, effectiveHeight=${effectiveHeight}`);
          }
        }
        
        rowHeight = Math.max(rowHeight, effectiveHeight);
        
        if (isCheckboxList) {
          console.log(`🔵   RENDER: rowHeight: ${rowHeight}`);
          if (field.code === 'SINK_TYPE') {
            console.log(`🔵🔵🔵 SINK_TYPE RENDER: final rowHeight=${rowHeight}`);
          }
        }
        if (field.code === 'SINK_UNDERMOUNT') {
          console.log(`🔵🔵🔵 SINK_UNDERMOUNT RENDER: final rowHeight=${rowHeight}`);
        }
      }
      
      // Check if we need a new page
      if (currentY - rowHeight < pageMargin) {
        if (showSectionBorders) {
          // Draw bottom border for continued section
          page.drawLine({
            start: { x: pageMargin, y: pageMargin },
            end: { x: pageMargin + contentWidth, y: pageMargin },
            thickness: 1,
            color: rgb(0.7, 0.7, 0.7),
          });
          // Draw white rectangle to cover left/right borders below the bottom border
          page.drawRectangle({
            x: pageMargin - 1,
            y: 0,
            width: contentWidth + 2,
            height: pageMargin - 1,
            color: rgb(1, 1, 1),
            borderWidth: 0,
          });
        }
        
        page = pdfDoc.addPage([595, 842]);
        const newHeaderHeight = await this.addHeaderToPage(
          page,
          pdfDoc,
          headerConfigurationJson,
          pageWidth,
          pageHeight,
          pageMargin,
          headerHeight,
          font,
          boldFont,
          headerData
        );
        currentY = pageHeight - (newHeaderHeight || headerImgHeight) - headerBottomMargin;
        
        sectionContinues = true;
        continuedSectionStartY = currentY;
        
        if (showSectionBorders) {
          // Draw top border for continued section
          page.drawLine({
            start: { x: pageMargin, y: currentY },
            end: { x: pageMargin + contentWidth, y: currentY },
            thickness: 1,
            color: rgb(0.7, 0.7, 0.7),
          });
          // Add buffer below top border so fields don't overlay it
          currentY -= 10;
        } else {
          // Even without borders, add buffer for consistency
          currentY -= 10;
        }
      }
      
      // Calculate field widths for this row
      // Check if field has fieldOnNextLine - if so, treat as full-width
      const firstFieldConfig = this.parseFieldConfiguration(rowFields[0]);
      const hasFieldOnNextLine = firstFieldConfig?.fieldOnNextLine || false;
      const isFullWidth = rowFields.length === 1 && (hasFieldOnNextLine || (rowFields[0].columnNumber || 0) === 0);
      const fieldGap = 10;
      const availableWidth = contentWidth - (sectionPadding * 2);
      
      // Use section-wide consistent label width for all fields in this section
      const maxLabelWidth = sectionMaxLabelWidth;
      
      if (isFullWidth) {
        // Full-width field
        await this.addEditableFieldToPDF(
          page,
          form,
          rowFields[0],
          formData,
          pageMargin + sectionPadding,
          currentY,
          availableWidth,
          fieldHeight,
          font,
          maxLabelWidth,
          3, // Default gap
          defaultFontSize,
          labelFontSize,
          multilineFontSize,
          checkboxListColumns
        );
      } else {
        // Column fields - determine actual columns needed based on field columnNumbers
        // Find the maximum column number used in this row
        const maxColumnNumber = Math.max(...rowFields.map(f => f.columnNumber || 0));
        const actualColumns = Math.max(maxColumnNumber, numberOfColumns || 2);

        const columnGap = 10; // Gap between columns
        const columnWidth = (availableWidth - (columnGap * (actualColumns - 1))) / actualColumns;
        let currentX = pageMargin + sectionPadding;

        // Render fields in their designated columns
        for (let columnNum = 1; columnNum <= actualColumns; columnNum++) {
          // Find field(s) for this column number
          const columnFields = rowFields.filter(f => {
            const fieldCol = f.columnNumber || 0;
            return fieldCol === columnNum;
          });

          if (columnFields.length > 0) {
            // Render fields in this column
            for (const field of columnFields) {
              await this.addEditableFieldToPDF(
                page,
                form,
                field,
                formData,
                currentX,
                currentY,
                columnWidth,
                fieldHeight,
                font,
                maxLabelWidth,
                3, // Default gap
                defaultFontSize,
                labelFontSize,
                multilineFontSize,
                checkboxListColumns
              );
            }
          }
          // Move to next column position
          currentX += columnWidth + (columnNum < actualColumns ? columnGap : 0);
        }
      }
      
      const renderHasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
      const renderHasCheckboxList = rowFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
      const renderHasSinkType = rowFields.some((f: any) => f.code === 'SINK_TYPE');
      const renderHasSinkUndermount = rowFields.some((f: any) => f.code === 'SINK_UNDERMOUNT');
      
      // Check if next row has a horizontal rule (to reduce spacing before it)
      const nextRowHasHorizontalRule = rowIndex < fieldRows.length - 1 && 
        fieldRows[rowIndex + 1].some((f: any) => this.normalizeFieldType(f.fieldType) === 'HorizontalRule');
      
      // Check for spacingAfter configuration on any field in this row
      let renderSpacingAfter = 0;
      for (const field of rowFields) {
        const fieldConfig = this.parseFieldConfiguration(field);
        const spacingAfter = fieldConfig.spacingAfter || 0;
        if (spacingAfter > 0 || renderHasSinkType || renderHasSinkUndermount) {
          console.log(`🔵 spacingAfter: field=${field.code}, spacingAfter=${spacingAfter}, config=`, fieldConfig);
        }
        renderSpacingAfter = Math.max(renderSpacingAfter, spacingAfter);
      }
      if (renderSpacingAfter > 0 || renderHasSinkType || renderHasSinkUndermount) {
        console.log(`🔵 ROW ${rowIndex}: renderSpacingAfter=${renderSpacingAfter}`);
      }
      
      // Debug logging for FLOORING_TYPE_BEHIND_ISLAND specifically
      const hasFlooringTypeBehindIsland = rowFields.some((f: any) => f.code === 'FLOORING_TYPE_BEHIND_ISLAND');
      if (hasFlooringTypeBehindIsland) {
        console.log(`🔵 FLOORING_TYPE_BEHIND_ISLAND ROW: rowFields=`, rowFields.map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)})`));
        console.log(`🔵 FLOORING_TYPE_BEHIND_ISLAND: renderHasCheckboxList=${renderHasCheckboxList}, fieldType=${rowFields.find((f: any) => f.code === 'FLOORING_TYPE_BEHIND_ISLAND')?.fieldType}`);
      }
      
      // Add extra spacing for checkbox lists
      // Note: Don't add extraLabelHeight to spacing below - the wrapped label already takes up space naturally
      // The checkbox list height doesn't include extraLabelHeight, so spacing is consistent
      // Reduce spacing before horizontal rules (use quarter of fieldSpacing)
      const renderBaseSpacing = nextRowHasHorizontalRule ? fieldSpacing / 4 : fieldSpacing;
      const extraSpacing = renderHasCheckboxList ? checkboxListSpacing : 0;
      const totalSpacing = renderBaseSpacing + extraSpacing + renderSpacingAfter;
      
      if (renderHasSinkType || renderHasSinkUndermount) {
        console.log(`🔵🔵🔵 SINK RENDER ROW ${rowIndex}: rowFields=`, rowFields.map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)}, col=${f.columnNumber || 0})`));
        console.log(`🔵🔵🔵 SINK RENDER ROW ${rowIndex}: renderHasCheckboxList=${renderHasCheckboxList}, renderHasSinkType=${renderHasSinkType}, renderHasSinkUndermount=${renderHasSinkUndermount}`);
        console.log(`🔵🔵🔵 SINK RENDER ROW ${rowIndex}: rowHeight=${rowHeight}, fieldSpacing=${fieldSpacing}, checkboxListSpacing=${checkboxListSpacing}, extraSpacing=${extraSpacing}, renderSpacingAfter=${renderSpacingAfter}, totalSpacing=${totalSpacing}`);
      }
      
      if (renderHasBlackEdgeTape || renderHasCheckboxList || hasFlooringTypeBehindIsland || renderHasSinkType || renderHasSinkUndermount) {
        console.log(`🔵 RENDER: Adjusting currentY - before: ${currentY}, rowHeight: ${rowHeight}, fieldSpacing: ${fieldSpacing}, extraSpacing: ${extraSpacing}, totalSpacing: ${totalSpacing}, subtracting: ${rowHeight + totalSpacing}`);
        if (renderHasBlackEdgeTape) {
          console.log(`🔵🔵🔵 RENDER BLACK_EDGE_TAPE: before currentY=${currentY}, rowHeight=${rowHeight}, fieldSpacing=${fieldSpacing}, extraSpacing=${extraSpacing}`);
        }
        if (hasFlooringTypeBehindIsland) {
          console.log(`🔵🔵🔵 FLOORING_TYPE_BEHIND_ISLAND: renderHasCheckboxList=${renderHasCheckboxList}, extraSpacing=${extraSpacing}, totalSpacing=${totalSpacing}`);
        }
        if (renderHasSinkType || renderHasSinkUndermount) {
          console.log(`🔵🔵🔵 SINK RENDER: before currentY=${currentY}, rowHeight=${rowHeight}, fieldSpacing=${fieldSpacing}, extraSpacing=${extraSpacing}, totalSpacing=${totalSpacing}, subtracting=${rowHeight + totalSpacing}`);
        }
      }
      
      currentY -= rowHeight + totalSpacing;
      
      if (renderHasBlackEdgeTape || renderHasCheckboxList || hasFlooringTypeBehindIsland || renderHasSinkType || renderHasSinkUndermount) {
        console.log(`🔵 RENDER: Adjusting currentY - after: ${currentY}`);
        if (renderHasBlackEdgeTape) {
          console.log(`🔵🔵🔵 RENDER BLACK_EDGE_TAPE: after currentY=${currentY}, spacing added: ${rowHeight + totalSpacing}`);
        }
        if (hasFlooringTypeBehindIsland) {
          console.log(`🔵🔵🔵 FLOORING_TYPE_BEHIND_ISLAND: after currentY=${currentY}, spacing added: ${rowHeight + totalSpacing}, totalSpacing=${totalSpacing}`);
        }
        if (renderHasSinkType || renderHasSinkUndermount) {
          console.log(`🔵🔵🔵 SINK RENDER: after currentY=${currentY}, spacing added: ${rowHeight + totalSpacing}, totalSpacing=${totalSpacing}`);
          if (rowIndex < fieldRows.length - 1) {
            console.log(`🔵🔵🔵 SINK RENDER NEXT ROW (${rowIndex + 1}) fields:`, fieldRows[rowIndex + 1].map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)}, col=${f.columnNumber || 0})`));
          }
        }
      }
      
      // If this is the last row and section continued, draw side borders
      if (showSectionBorders && sectionContinues && rowIndex === fieldRows.length - 1) {
        page.drawLine({
          start: { x: pageMargin, y: continuedSectionStartY },
          end: { x: pageMargin, y: currentY },
          thickness: 1,
          color: rgb(0.7, 0.7, 0.7),
        });
        page.drawLine({
          start: { x: pageMargin + contentWidth, y: continuedSectionStartY },
          end: { x: pageMargin + contentWidth, y: currentY },
          thickness: 1,
          color: rgb(0.7, 0.7, 0.7),
        });
        // Draw bottom border
        page.drawLine({
          start: { x: pageMargin, y: currentY },
          end: { x: pageMargin + contentWidth, y: currentY },
          thickness: 1,
          color: rgb(0.7, 0.7, 0.7),
        });
      }
    }

    return { page, y: currentY };
  }

  /**
   * Renders sections in columns side by side
   */
  private static async renderColumnSections(
    sections: any[],
    currentPage: PDFPage,
    pdfDoc: PDFDocument,
    startY: number,
    pageMargin: number,
    contentWidth: number,
    fieldHeight: number,
    fieldSpacing: number,
    checkboxListSpacing: number,
    sectionSpacing: number,
    sectionPadding: number,
    font: PDFFont,
    boldFont: PDFFont,
    form: PDFForm,
    formData: any,
    pageWidth: number,
    pageHeight: number,
    headerImgHeight: number,
    headerConfigurationJson: string | undefined,
    headerHeight: number,
    headerBottomMargin: number,
    numberOfColumns: number,
    defaultFontSize: number,
    labelFontSize: number,
    multilineFontSize: number,
    sectionTitleFontSize: number,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {},
    showSectionBorders: boolean = true,
    checkboxListColumns: number = 3
  ): Promise<{ page: PDFPage, y: number }> {
    let page = currentPage;
    let currentY = startY;

    // Group sections by column number
    const sectionsByColumn: { [key: number]: any[] } = {};
    sections.forEach((section: any) => {
      const colNum = section.columnNumber || 1;
      if (!sectionsByColumn[colNum]) {
        sectionsByColumn[colNum] = [];
      }
      sectionsByColumn[colNum].push(section);
    });

    const columnNumbers = Object.keys(sectionsByColumn).map(k => parseInt(k)).sort();
    const numColumns = columnNumbers.length;
    const columnGap = 10;
    const columnWidth = (contentWidth - (columnGap * (numColumns - 1))) / numColumns;

    // Track Y position for each column independently
    const columnYPositions: { [key: number]: number } = {};
    const columnData: { [key: number]: { sections: any[], x: number } } = {};
    // Track last section border info for each column (to extend border to minY)
    const lastSectionBorders: { [key: number]: { sectionTopY: number, sectionBottomY: number, colX: number } } = {};

    // Initialize column data
    columnNumbers.forEach(colNum => {
      columnYPositions[colNum] = currentY;
      columnData[colNum] = {
        sections: sectionsByColumn[colNum],
        x: pageMargin + ((colNum - 1) * (columnWidth + columnGap))
      };
    });

    // Find the maximum number of sections in any column to iterate
    const maxSections = Math.max(...columnNumbers.map(cn => sectionsByColumn[cn].length));

    // Process sections row by row (section index) across all columns
    for (let sectionIndex = 0; sectionIndex < maxSections; sectionIndex++) {
      // Process this section index for each column
      for (const colNum of columnNumbers) {
        const columnSections = sectionsByColumn[colNum];
        if (sectionIndex >= columnSections.length) continue; // This column has no section at this index

        const section = columnSections[sectionIndex];
        const colX = columnData[colNum].x;
        let colY = columnYPositions[colNum];

        // Check if we need a new page
        if (colY - 25 < pageMargin) {
          page = pdfDoc.addPage([595, 842]);
          // Reset all column positions to the same starting point
          columnNumbers.forEach(cn => {
            columnYPositions[cn] = pageHeight - headerImgHeight - headerBottomMargin;
          });
          colY = columnYPositions[colNum];
          const newHeaderHeight = await this.addHeaderToPage(
            page,
            pdfDoc,
            headerConfigurationJson,
            pageWidth,
            pageHeight,
            pageMargin,
            headerHeight,
            font,
            boldFont,
            headerData
          );
          if (newHeaderHeight) {
            columnNumbers.forEach(cn => {
              columnYPositions[cn] = pageHeight - newHeaderHeight - headerBottomMargin;
            });
            colY = columnYPositions[colNum];
          }
        }

        // Add spacing between sections within the same column (except for the first section)
        if (sectionIndex > 0) {
          colY -= sectionSpacing; // Use sectionSpacing for spacing between sections in columns
        }

        // Get fields for this section
        const sectionFields = section.formFields
          ?.filter((field: any) => field.isActive)
          ?.sort((a: any, b: any) => a.displayOrder - b.displayOrder) || [];

        console.log(`PDF Section "${section.description}" has ${sectionFields.length} active fields:`,
          sectionFields.map((f: any) => `${f.code} (${f.fieldType})`));

        // Group fields into rows based on displayOrder and columnNumber
        const fieldRows: any[][] = [];
        let currentRow: any[] = [];
        let lastFieldColumn = 0;

        for (let i = 0; i < sectionFields.length; i++) {
          const field = sectionFields[i];
          const fieldColumn = field.columnNumber || 0;

          // If this field has columnNumber > 0, it's a column field
          if (fieldColumn > 0) {
            // Check if we should start a new row
            if (currentRow.length > 0 && fieldColumn <= lastFieldColumn) {
              // Start a new row
              fieldRows.push(currentRow);
              currentRow = [field];
            } else {
              // Add to current row
              currentRow.push(field);
            }
            lastFieldColumn = fieldColumn;
          } else {
            // Full-width field - finish current row and add this as a separate row
            if (currentRow.length > 0) {
              fieldRows.push(currentRow);
              currentRow = [];
            }
            fieldRows.push([field]); // Full-width field is its own row
            lastFieldColumn = 0;
          }
        }

        // Don't forget the last row
        if (currentRow.length > 0) {
          fieldRows.push(currentRow);
        }

        // Calculate consistent label width for ALL fields in this section for alignment
        const availableWidth = columnWidth - (sectionPadding * 2);
        const sectionMaxLabelWidth = this.calculateMaxLabelWidth(sectionFields, availableWidth, font);

        // Calculate total height based on rows (each row has one height, not per-field, accounting for wrapped labels)
        let totalRowHeight = 0;
        for (let rowIdx = 0; rowIdx < fieldRows.length; rowIdx++) {
          const rowFields = fieldRows[rowIdx];
          const hasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
          if (hasBlackEdgeTape) {
            console.log(`🔵🔵🔵 COLUMN BLACK_EDGE_TAPE ROW ${rowIdx}: fields in this row:`, rowFields.map((f: any) => f.code));
          }
          let rowHeight = 0;
          let checkboxListExtraLabelHeight = 0; // Track extraLabelHeight for checkbox lists in this row
          for (const field of rowFields) {
            const fieldType = this.normalizeFieldType(field.fieldType);
            const isMultiline = fieldType === 'Multiline';
            const isCheckbox = fieldType === 'Bool';
            const isCheckboxList = fieldType === 'CheckboxList';

            // Calculate label height to check if it wraps (only add extra height if wrapping)
            const labelHeight = this.calculateLabelHeight(field, sectionMaxLabelWidth - 5, font);
            const baseLabelHeight = 8; // Base label font size
            // Only add extra height if label wraps beyond single line
            const extraLabelHeight = Math.max(0, labelHeight - baseLabelHeight);

            // Track extraLabelHeight for checkbox lists (will be added to spacing below, not height)
            if (isCheckboxList && extraLabelHeight > 0) {
              checkboxListExtraLabelHeight = Math.max(checkboxListExtraLabelHeight, extraLabelHeight);
            }

            const fieldConfig = this.parseFieldConfiguration(field);
            let effectiveHeight = fieldHeight;
            if (isMultiline) {
              const configuredMultilineFontSize =
                fieldConfig.fontSize ?? multilineFontSize ?? PDFService.DEFAULT_MULTILINE_FONT_SIZE;
              const configuredMultilineLines =
                fieldConfig.multilineLines ?? PDFService.MULTILINE_LINES;
              effectiveHeight = this.calculateCustomMultilineHeight(
                configuredMultilineFontSize,
                configuredMultilineLines
              );
            } else if (isCheckbox) {
              effectiveHeight = 10;
            } else if (isCheckboxList) {
              // Calculate height needed for checkbox list using dynamic row heights
              // This matches the rendering approach - calculate actual height of each row
              const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
              const fieldNumColumns = fieldConfig.checkboxColumns ?? checkboxListColumns;
              const numColumns = fieldNumColumns;
              const numRows = Math.ceil(items.length / numColumns);
              const checkboxSize = 10; // Matches regular checkboxes
              const labelFontSize = 8; // Match rendering labelFontSize
              const lineSpacing = 2; // Spacing between wrapped lines
              const checkboxLabelSpacing = 2; // Space between checkbox and its label text

              console.log(`🔵 COLUMN CHECKBOX LIST HEIGHT CALC: ${field.code} (${field.description})`);
              if (field.code === 'BLACK_EDGE_TAPE') {
                console.log(`🔵🔵🔵 COLUMN BLACK_EDGE_TAPE HEIGHT CALCULATION START 🔵🔵🔵`);
              }
              console.log(`🔵 COLUMN   items.length: ${items.length}, numRows: ${numRows}, numColumns: ${numColumns}`);
              
              // Calculate column width for wrapping calculation
              // For checkbox lists, use the actual field width (availableWidth minus label area and gap)
              // If field has a label, reserve space for it; otherwise use full width
              const fieldHasLabel = field.description && field.description.trim().length > 0;
              const labelFieldGap = 3; // Same as in rendering
              const columnGap = 10; // Horizontal gap between columns
              const actualFieldWidth = fieldHasLabel 
                ? availableWidth - sectionMaxLabelWidth - labelFieldGap
                : availableWidth;
              const columnWidth = (actualFieldWidth - (columnGap * (numColumns - 1))) / numColumns;
              const maxLabelWidth = columnWidth - checkboxSize - checkboxLabelSpacing - 1; // checkboxSize + checkboxLabelSpacing + padding
              
              console.log(`🔵 COLUMN   availableWidth: ${availableWidth}, sectionMaxLabelWidth: ${sectionMaxLabelWidth}, fieldHasLabel: ${fieldHasLabel}`);
              console.log(`🔵 COLUMN   actualFieldWidth: ${actualFieldWidth}, columnWidth: ${columnWidth}, maxLabelWidth: ${maxLabelWidth}`);
              
              // Calculate height for each row based on tallest label in that row
              const rowHeights: number[] = [];
              
              for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
                let maxRowHeight = checkboxSize; // Base height is checkbox size
                
                // Check all items in this row (items in different columns but same row)
                for (let colIndex = 0; colIndex < numColumns; colIndex++) {
                  const itemIndex = rowIndex * numColumns + colIndex;
                  if (itemIndex >= items.length) break;
                  
                  const item = items[itemIndex];
                  const labelText = item.description || '';
                  
                  // Calculate how many lines this label needs
                  const numLines = this.calculateCheckboxListItemLines(labelText, maxLabelWidth, font, labelFontSize);
                  
                  console.log(`🔵 COLUMN   item ${itemIndex} (row ${rowIndex}, col ${colIndex}): "${labelText}" -> ${numLines} lines`);
                  
                  // Calculate height needed for this label (accounting for wrapped lines)
                  const labelHeight = numLines === 1 
                    ? labelFontSize // Single line
                    : labelFontSize + ((numLines - 1) * (labelFontSize + lineSpacing)); // Wrapped lines
                  
                  // Row height is the maximum of checkbox height and label height
                  const itemHeight = Math.max(checkboxSize, labelHeight);
                  maxRowHeight = Math.max(maxRowHeight, itemHeight);
                }
                
                // Add minimum spacing between rows (except for the last row, which doesn't need spacing after it)
                // The spacing will be handled by the field spacing below the checkbox list
                const isLastRow = rowIndex === numRows - 1;
                rowHeights.push(maxRowHeight + (isLastRow ? 0 : 2));
                
                console.log(`🔵 COLUMN   row ${rowIndex}: maxRowHeight=${maxRowHeight}, isLastRow=${isLastRow}, rowHeight=${rowHeights[rowIndex]}`);
              }
              
              // Sum up all row heights
              effectiveHeight = rowHeights.reduce((sum, height) => sum + height, 0);
              
              console.log(`🔵 COLUMN   rowHeights:`, rowHeights);
              console.log(`🔵 COLUMN   effectiveHeight: ${effectiveHeight} (sum of all row heights)`);
            }
            
            // Only add extra height if label wraps (maintains original spacing for single-line labels)
            // For checkbox lists, the checkbox items are positioned below the label with a 2pt gap
            // The label baseline is at y - height + 12, and checkboxes start at y - height + 10
            // So checkboxes start at (height - 10)pt below the field top
            // The last checkbox's top is at y - height + 10 - cumulativeHeight
            // The last checkbox's bottom is at (y - height + 10 - cumulativeHeight) - checkboxSize = y - height - cumulativeHeight
            // So the total height from field top to bottom of last checkbox is height + cumulativeHeight
            if (isCheckboxList) {
              // For checkbox lists, the checkbox items start at y - height + 10
              // The sum of row heights already includes the spacing between rows
              // We need to add:
              // 1. The distance from the field top to where checkboxes start: (height - 10)
              // 2. The checkbox size itself (10pt) to account for the checkbox extending below its Y position
              const checkboxSize = 10; // Size of checkbox
              const checkboxStartOffset = fieldHeight - 10; // Distance from field top to checkbox start (for height=16, this is 6pt)
              effectiveHeight = effectiveHeight + checkboxStartOffset + checkboxSize;
              console.log(`🔵 COLUMN   ${field.code} checkbox list: effectiveHeight=${effectiveHeight} (sum of row heights + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
            } else {
              effectiveHeight = effectiveHeight + extraLabelHeight;
            }
            
            rowHeight = Math.max(rowHeight, effectiveHeight);
            
            if (isCheckboxList) {
              console.log(`🔵 COLUMN   rowHeight: ${rowHeight} (max of ${effectiveHeight})`);
            }
          }
          
          const calcHasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
          const calcHasCheckboxList = rowFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
          
          // Check if next row has a horizontal rule (to reduce spacing before it)
          const calcColNextRowHasHorizontalRule = rowIdx < fieldRows.length - 1 && 
            fieldRows[rowIdx + 1].some((f: any) => this.normalizeFieldType(f.fieldType) === 'HorizontalRule');
          
          // Check for spacingAfter configuration on any field in this row
          let calcSpacingAfter = 0;
          for (const field of rowFields) {
            const fieldConfig = this.parseFieldConfiguration(field);
            const spacingAfter = fieldConfig.spacingAfter || 0;
            calcSpacingAfter = Math.max(calcSpacingAfter, spacingAfter);
          }
          
          // Add extra spacing for checkbox lists
          // Note: Don't add extraLabelHeight to spacing below - the wrapped label already takes up space naturally
          // The checkbox list height doesn't include extraLabelHeight, so spacing is consistent
          // Reduce spacing before horizontal rules (use quarter of fieldSpacing)
          const calcColBaseSpacing = calcColNextRowHasHorizontalRule ? fieldSpacing / 4 : fieldSpacing;
          const calcExtraSpacing = calcHasCheckboxList ? checkboxListSpacing : 0;
          const calcTotalSpacing = calcColBaseSpacing + calcExtraSpacing + calcSpacingAfter;
          
          if (calcHasBlackEdgeTape || calcHasCheckboxList) {
            console.log(`🔵 COLUMN   ROW ${rowIdx}: fieldSpacing: ${fieldSpacing}, extraSpacing: ${calcExtraSpacing}, totalSpacing: ${calcTotalSpacing}, rowHeight before spacing: ${rowHeight}`);
            console.log(`🔵 COLUMN   ROW ${rowIdx}: totalRowHeight before adding: ${totalRowHeight}, adding ${rowHeight + calcTotalSpacing}`);
          }
          
          totalRowHeight += rowHeight + calcTotalSpacing;
          
          if (calcHasBlackEdgeTape || calcHasCheckboxList) {
            console.log(`🔵 COLUMN   ROW ${rowIdx}: totalRowHeight after adding: ${totalRowHeight}`);
            if (calcHasBlackEdgeTape) {
              console.log(`🔵🔵🔵 COLUMN BLACK_EDGE_TAPE ROW ${rowIdx} COMPLETE: rowHeight=${rowHeight}, totalSpacing=${calcTotalSpacing}, totalRowHeight=${totalRowHeight}`);
              if (rowIdx < fieldRows.length - 1) {
                console.log(`🔵🔵🔵 COLUMN NEXT ROW (${rowIdx + 1}) fields:`, fieldRows[rowIdx + 1].map((f: any) => f.code));
              } else {
                console.log(`🔵🔵🔵 COLUMN BLACK_EDGE_TAPE is in LAST ROW of section`);
              }
            }
          }
        }
        
        // Calculate section height to draw border
        const sectionHeight = totalRowHeight + 15;
        
        // Log section height if it contains checkbox lists or BLACK_EDGE_TAPE
        const hasCheckboxList = sectionFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
        const hasBlackEdgeTape = sectionFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
        if (hasCheckboxList || hasBlackEdgeTape) {
          console.log(`🔵 COLUMN SECTION "${section.description}": totalRowHeight: ${totalRowHeight}, sectionHeight: ${sectionHeight} (totalRowHeight + 15)`);
          if (hasBlackEdgeTape) {
            console.log(`🔵🔵🔵 COLUMN BLACK_EDGE_TAPE SECTION HEIGHT: totalRowHeight=${totalRowHeight}, sectionHeight=${sectionHeight} (includes +15 padding)`);
          }
        }
        const sectionTopY = colY;
        const sectionBottomY = colY - sectionHeight;
        
        // Check if this is the last section in this column
        const isLastSectionInColumn = sectionIndex === columnSections.length - 1;
        
        // Draw section border (gray rectangle) - only if showSectionBorders is true
        if (showSectionBorders) {
          page.drawRectangle({
            x: colX,
            y: sectionBottomY,
            width: columnWidth,
            height: sectionHeight,
            borderColor: rgb(0.7, 0.7, 0.7),
            borderWidth: 1,
          });
        }
        
        // Store border info for last section in column (will extend to minY later) - only if borders are shown
        if (isLastSectionInColumn && showSectionBorders) {
          lastSectionBorders[colNum] = {
            sectionTopY: sectionTopY,
            sectionBottomY: sectionBottomY,
            colX: colX
          };
        }
        
        // Draw white background for section title (only if description exists and borders are shown)
        // Check for both null/undefined and empty string
        const hasDescription = !!(section.description && section.description.trim().length > 0);
        if (hasDescription && showSectionBorders) {
          const titleWidth = boldFont.widthOfTextAtSize(section.description, sectionTitleFontSize) + 8;
          page.drawRectangle({
            x: colX + 5,
            y: colY - 7,
            width: titleWidth,
            height: 12,
            color: rgb(1, 1, 1),
            borderWidth: 0,
          });

          // Add section header on top of border
          page.drawText(section.description, {
            x: colX + 8,
            y: colY - 3,
            size: sectionTitleFontSize,
            font: boldFont,
            color: rgb(0, 0, 0),
          });
        }
        // Reduce gap before first field if section has no description
        // hasDescription is already defined above
        console.log(`🔵🔵🔵 COLUMN RENDER SECTION "${section.description}": hasDescription: ${String(hasDescription)}, colY before adjustment: ${colY}`);
        colY -= hasDescription ? 18 : 10;
        console.log(`🔵🔵🔵 COLUMN RENDER SECTION "${section.description}": colY after adjustment: ${colY} (reduced by ${hasDescription ? 18 : 6})`);

        // Render all fields in this section with padding
        let sectionContinues = false;
        let continuedSectionStartY = 0;
        
        // Render each row
        for (let rowIndex = 0; rowIndex < fieldRows.length; rowIndex++) {
          const rowFields = fieldRows[rowIndex];
          
          // Calculate row height based on tallest field in this row (accounting for wrapped labels)
          let rowHeight = 0;
          let checkboxListExtraLabelHeight = 0; // Track extraLabelHeight for checkbox lists in this row
          for (const field of rowFields) {
            const fieldType = this.normalizeFieldType(field.fieldType);
            const isMultiline = fieldType === 'Multiline';
            const isCheckbox = fieldType === 'Bool';
            const isCheckboxList = fieldType === 'CheckboxList';
            
            // Calculate label height to check if it wraps (only add extra height if wrapping)
            const labelHeight = this.calculateLabelHeight(field, sectionMaxLabelWidth - 5, font);
            const baseLabelHeight = 8; // Base label font size
            // Only add extra height if label wraps beyond single line
            const extraLabelHeight = Math.max(0, labelHeight - baseLabelHeight);
            
            // Track extraLabelHeight for checkbox lists (will be added to spacing below, not height)
            if (isCheckboxList && extraLabelHeight > 0) {
              checkboxListExtraLabelHeight = Math.max(checkboxListExtraLabelHeight, extraLabelHeight);
            }
            
            const fieldConfig = this.parseFieldConfiguration(field);
            let effectiveHeight = fieldHeight;
            if (isMultiline) {
              const configuredMultilineFontSize =
                fieldConfig.fontSize ?? multilineFontSize ?? PDFService.DEFAULT_MULTILINE_FONT_SIZE;
              const configuredMultilineLines =
                fieldConfig.multilineLines ?? PDFService.MULTILINE_LINES;
              effectiveHeight = this.calculateCustomMultilineHeight(
                configuredMultilineFontSize,
                configuredMultilineLines
              );
            } else if (isCheckbox) {
              effectiveHeight = 10;
            } else if (isCheckboxList) {
              // Calculate height needed for checkbox list using dynamic row heights
              // This matches the rendering approach - calculate actual height of each row
              const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
              const fieldNumColumns = fieldConfig.checkboxColumns ?? checkboxListColumns;
              const numColumns = fieldNumColumns;
              const numRows = Math.ceil(items.length / numColumns);
              const checkboxSize = 10; // Matches regular checkboxes
              const labelFontSize = 8; // Match rendering labelFontSize
              const lineSpacing = 2; // Spacing between wrapped lines
              const checkboxLabelSpacing = 2; // Space between checkbox and its label text

              // Calculate column width for wrapping calculation
              // For checkbox lists, use the actual field width (availableWidth minus label area and gap)
              // If field has a label, reserve space for it; otherwise use full width
              const fieldHasLabel = field.description && field.description.trim().length > 0;
              const labelFieldGap = 3; // Same as in rendering
              // Calculate availableWidth from columnWidth and sectionPadding (since it's redeclared later in the loop)
              const calcAvailableWidth = columnWidth - (sectionPadding * 2);
              const columnGap = 10; // Horizontal gap between columns
              const actualFieldWidth = fieldHasLabel 
                ? calcAvailableWidth - sectionMaxLabelWidth - labelFieldGap
                : calcAvailableWidth;
              const checkboxColumnWidth = (actualFieldWidth - (columnGap * (numColumns - 1))) / numColumns;
              const maxLabelWidth = checkboxColumnWidth - checkboxSize - checkboxLabelSpacing - 1; // checkboxSize + checkboxLabelSpacing + padding
              
              // Calculate height for each row based on tallest label in that row
              const rowHeights: number[] = [];
              
              for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
                let maxRowHeight = checkboxSize; // Base height is checkbox size
                
                // Check all items in this row (items in different columns but same row)
                for (let colIndex = 0; colIndex < numColumns; colIndex++) {
                  const itemIndex = rowIndex * numColumns + colIndex;
                  if (itemIndex >= items.length) break;
                  
                  const item = items[itemIndex];
                  const labelText = item.description || '';
                  
                  // Calculate how many lines this label needs
                  const numLines = this.calculateCheckboxListItemLines(labelText, maxLabelWidth, font, labelFontSize);
                  
                  // Calculate height needed for this label (accounting for wrapped lines)
                  const labelHeight = numLines === 1 
                    ? labelFontSize // Single line
                    : labelFontSize + ((numLines - 1) * (labelFontSize + lineSpacing)); // Wrapped lines
                  
                  // Row height is the maximum of checkbox height and label height
                  const itemHeight = Math.max(checkboxSize, labelHeight);
                  maxRowHeight = Math.max(maxRowHeight, itemHeight);
                }
                
                // Add minimum spacing between rows (except for the last row, which doesn't need spacing after it)
                // The spacing will be handled by the field spacing below the checkbox list
                const isLastRow = rowIndex === numRows - 1;
                rowHeights.push(maxRowHeight + (isLastRow ? 0 : 2));
              }
              
              // Sum up all row heights
              effectiveHeight = rowHeights.reduce((sum, height) => sum + height, 0);
              
              console.log(`🔵 COLUMN RENDER: ${field.code} rowHeights:`, rowHeights);
              console.log(`🔵 COLUMN RENDER: ${field.code} effectiveHeight: ${effectiveHeight} (sum of all row heights)`);
            }
            
            // Only add extra height if label wraps (maintains original spacing for single-line labels)
            // For checkbox lists, the checkbox items are positioned below the label with a 2pt gap
            // The label baseline is at y - height + 12, and checkboxes start at y - height + 10
            // So checkboxes start at (height - 10)pt below the field top
            // The last checkbox's top is at y - height + 10 - cumulativeHeight
            // The last checkbox's bottom is at (y - height + 10 - cumulativeHeight) - checkboxSize = y - height - cumulativeHeight
            // So the total height from field top to bottom of last checkbox is height + cumulativeHeight
            if (isCheckboxList) {
              // For checkbox lists, the checkbox items start at y - height + 10
              // The sum of row heights already includes the spacing between rows
              // We need to add:
              // 1. The distance from the field top to where checkboxes start: (height - 10)
              // 2. The checkbox size itself (10pt) to account for the checkbox extending below its Y position
              const checkboxSize = 10; // Size of checkbox
              const checkboxStartOffset = fieldHeight - 10; // Distance from field top to checkbox start (for height=16, this is 6pt)
              effectiveHeight = effectiveHeight + checkboxStartOffset + checkboxSize;
              console.log(`🔵 COLUMN RENDER: ${field.code} checkbox list: effectiveHeight=${effectiveHeight} (sum of row heights + ${checkboxStartOffset}pt offset + ${checkboxSize}pt checkbox size)`);
            } else {
              effectiveHeight = effectiveHeight + extraLabelHeight;
            }
            
            rowHeight = Math.max(rowHeight, effectiveHeight);
            
            if (isCheckboxList) {
              console.log(`🔵 COLUMN RENDER: ${field.code} rowHeight: ${rowHeight}`);
            }
          }
          
          // Check if we need a new page
          if (colY - rowHeight < pageMargin) {
            if (showSectionBorders) {
              // Draw bottom border for continued section
              page.drawLine({
                start: { x: colX, y: pageMargin },
                end: { x: colX + columnWidth, y: pageMargin },
                thickness: 1,
                color: rgb(0.7, 0.7, 0.7),
              });
              // Draw white rectangle to cover left/right borders below the bottom border
              page.drawRectangle({
                x: colX - 1,
                y: 0,
                width: columnWidth + 2,
                height: pageMargin - 1,
                color: rgb(1, 1, 1),
                borderWidth: 0,
              });
            }
            
            page = pdfDoc.addPage([595, 842]);
            // Reset all column positions to the same starting point
            columnNumbers.forEach(cn => {
              columnYPositions[cn] = pageHeight - headerImgHeight - headerBottomMargin;
            });
            colY = columnYPositions[colNum];
            const newHeaderHeight = await this.addHeaderToPage(
              page,
              pdfDoc,
              headerConfigurationJson,
              pageWidth,
              pageHeight,
              pageMargin,
              headerHeight,
              font,
              boldFont,
              headerData
            );
            if (newHeaderHeight) {
              columnNumbers.forEach(cn => {
                columnYPositions[cn] = pageHeight - newHeaderHeight - headerBottomMargin;
              });
              colY = columnYPositions[colNum];
            }
            
            sectionContinues = true;
            continuedSectionStartY = colY;
            
            if (showSectionBorders) {
              // Draw top border for continued section
              page.drawLine({
                start: { x: colX, y: colY },
                end: { x: colX + columnWidth, y: colY },
                thickness: 1,
                color: rgb(0.7, 0.7, 0.7),
              });
              // Add buffer below top border so fields don't overlay it
              colY -= 10;
            } else {
              // Even without borders, add buffer for consistency
              colY -= 10;
            }
          }
          
          // Calculate field widths for this row
          const isFullWidth = rowFields.length === 1 && (rowFields[0].columnNumber || 0) === 0;
          const fieldGap = 10;
          const availableWidth = columnWidth - (sectionPadding * 2);
          
          // Use section-wide consistent label width for all fields in this section
          const maxLabelWidth = sectionMaxLabelWidth;
          
          if (isFullWidth) {
            // Full-width field
            await this.addEditableFieldToPDF(
              page,
              form,
              rowFields[0],
              formData,
              colX + sectionPadding,
              colY,
              availableWidth,
              fieldHeight,
              font,
              maxLabelWidth,
              3, // Default gap
              defaultFontSize,
              labelFontSize,
              multilineFontSize,
              checkboxListColumns
            );
          } else {
            // Column fields - divide width
            const numColumns = rowFields.length;
            const labelFieldGap = 3;
            const labelAreaWidth = maxLabelWidth + labelFieldGap; // Label area width (consistent for all fields)
            
            // Count how many fields have labels
            const fieldsWithLabels = rowFields.filter(f => f.description && f.description.trim() !== '');
            const firstFieldHasLabel = rowFields[0].description && rowFields[0].description.trim() !== '';
            const secondFieldHasLabel = rowFields.length > 1 && rowFields[1].description && rowFields[1].description.trim() !== '';
            
            // Special case: two columns with both having labels
            const isTwoColumnsWithBothLabels = numColumns === 2 && firstFieldHasLabel && secondFieldHasLabel;
            
            let currentX = colX + sectionPadding;
            
            if (isTwoColumnsWithBothLabels) {
              // Special layout for two columns with both labels:
              // Make both fields the same width with minimal gap between them
              const minGap = 5; // Minimal gap between fields
              const commonFieldWidth = (availableWidth - minGap) / 2;
              
              // First field: same width as second, with normal label gap
              await this.addEditableFieldToPDF(
                page,
                form,
                rowFields[0],
                formData,
                currentX,
                colY,
                commonFieldWidth,
                fieldHeight,
                font,
                maxLabelWidth,
                labelFieldGap, // Use normal gap
                defaultFontSize,
                labelFontSize,
                multilineFontSize,
                checkboxListColumns
              );
              currentX += commonFieldWidth + minGap;
              
              // Second field: same width as first, with no gap between label and input
              await this.addEditableFieldToPDF(
                page,
                form,
                rowFields[1],
                formData,
                currentX,
                colY,
                commonFieldWidth,
                fieldHeight,
                font,
                maxLabelWidth,
                0, // No gap between label and input
                defaultFontSize,
                labelFontSize,
                multilineFontSize,
                checkboxListColumns
              );
            } else {
              // Original logic for other cases
              // First column always reserves label area (for alignment), plus any additional fields with labels
              const additionalFieldsWithLabels = fieldsWithLabels.length - (firstFieldHasLabel ? 1 : 0);
              const totalLabelAreaWidth = labelAreaWidth + (additionalFieldsWithLabels * labelAreaWidth);
              
              const remainingWidth = availableWidth - totalLabelAreaWidth - (fieldGap * (numColumns - 1));
              // Each field's input area gets equal share of remaining space
              const inputWidthPerField = remainingWidth / numColumns;

              for (let colIndex = 0; colIndex < rowFields.length; colIndex++) {
                const field = rowFields[colIndex];
                const fieldHasLabel = field.description && field.description.trim() !== '';
                const isFirstColumn = colIndex === 0;

                // First column always gets label area (for alignment), other columns only if they have a label
                const shouldReserveLabelArea = isFirstColumn || fieldHasLabel;
                const fieldWidth = shouldReserveLabelArea
                  ? labelAreaWidth + inputWidthPerField
                  : inputWidthPerField;

                await this.addEditableFieldToPDF(
                  page,
                  form,
                  field,
                  formData,
                  currentX,
                  colY,
                  fieldWidth,
                  fieldHeight,
                  font,
                  shouldReserveLabelArea ? maxLabelWidth : undefined, // Only pass labelWidth if reserving label area
                  labelFieldGap, // Use normal gap
                  defaultFontSize,
                  labelFontSize,
                  multilineFontSize,
                  checkboxListColumns
                );

                // Move to next field position
                currentX += fieldWidth + (colIndex < rowFields.length - 1 ? fieldGap : 0);
              }
            }
          }
          
          const renderColHasBlackEdgeTape = rowFields.some((f: any) => f.code === 'BLACK_EDGE_TAPE');
          const renderColHasCheckboxList = rowFields.some((f: any) => this.normalizeFieldType(f.fieldType) === 'CheckboxList');
          
          // Check if next row has a horizontal rule (to reduce spacing before it)
          const renderColNextRowHasHorizontalRule = rowIndex < fieldRows.length - 1 && 
            fieldRows[rowIndex + 1].some((f: any) => this.normalizeFieldType(f.fieldType) === 'HorizontalRule');
          
          // Check for spacingAfter configuration on any field in this row
          let renderColSpacingAfter = 0;
          for (const field of rowFields) {
            const fieldConfig = this.parseFieldConfiguration(field);
            const spacingAfter = fieldConfig.spacingAfter || 0;
            if (spacingAfter > 0) {
              console.log(`🔵 COLUMN spacingAfter: field=${field.code}, spacingAfter=${spacingAfter}, config=`, fieldConfig);
            }
            renderColSpacingAfter = Math.max(renderColSpacingAfter, spacingAfter);
          }
          if (renderColSpacingAfter > 0) {
            console.log(`🔵 COLUMN ROW ${rowIndex}: renderColSpacingAfter=${renderColSpacingAfter}`);
          }
          
          // Debug logging for FLOORING_TYPE_BEHIND_ISLAND specifically
          const renderColHasFlooringTypeBehindIsland = rowFields.some((f: any) => f.code === 'FLOORING_TYPE_BEHIND_ISLAND');
          if (renderColHasFlooringTypeBehindIsland) {
            console.log(`🔵 COLUMN FLOORING_TYPE_BEHIND_ISLAND ROW: rowFields=`, rowFields.map((f: any) => `${f.code} (${this.normalizeFieldType(f.fieldType)})`));
            console.log(`🔵 COLUMN FLOORING_TYPE_BEHIND_ISLAND: renderColHasCheckboxList=${renderColHasCheckboxList}, fieldType=${rowFields.find((f: any) => f.code === 'FLOORING_TYPE_BEHIND_ISLAND')?.fieldType}`);
          }
          
          // Add extra spacing for checkbox lists
          // Note: Don't add extraLabelHeight to spacing below - the wrapped label already takes up space naturally
          // The checkbox list height doesn't include extraLabelHeight, so spacing is consistent
          // Reduce spacing before horizontal rules (use half of fieldSpacing)
          const renderColBaseSpacing = renderColNextRowHasHorizontalRule ? fieldSpacing / 2 : fieldSpacing;
          const renderColExtraSpacing = renderColHasCheckboxList ? checkboxListSpacing : 0;
          const renderColTotalSpacing = renderColBaseSpacing + renderColExtraSpacing + renderColSpacingAfter;
          
          if (renderColHasBlackEdgeTape || renderColHasCheckboxList || renderColHasFlooringTypeBehindIsland) {
            console.log(`🔵 COLUMN RENDER: Adjusting colY - before: ${colY}, rowHeight: ${rowHeight}, fieldSpacing: ${fieldSpacing}, extraSpacing: ${renderColExtraSpacing}, totalSpacing: ${renderColTotalSpacing}, subtracting: ${rowHeight + renderColTotalSpacing}`);
            if (renderColHasBlackEdgeTape) {
              console.log(`🔵🔵🔵 COLUMN RENDER BLACK_EDGE_TAPE: before colY=${colY}, rowHeight=${rowHeight}, fieldSpacing=${fieldSpacing}, extraSpacing=${renderColExtraSpacing}`);
            }
            if (renderColHasFlooringTypeBehindIsland) {
              console.log(`🔵🔵🔵 COLUMN FLOORING_TYPE_BEHIND_ISLAND: renderColHasCheckboxList=${renderColHasCheckboxList}, extraSpacing=${renderColExtraSpacing}, totalSpacing=${renderColTotalSpacing}`);
            }
          }
          
          colY -= rowHeight + renderColTotalSpacing;
          
          if (renderColHasBlackEdgeTape || renderColHasCheckboxList || renderColHasFlooringTypeBehindIsland) {
            console.log(`🔵 COLUMN RENDER: Adjusting colY - after: ${colY}`);
            if (renderColHasBlackEdgeTape) {
              console.log(`🔵🔵🔵 COLUMN RENDER BLACK_EDGE_TAPE: after colY=${colY}, spacing added: ${rowHeight + renderColTotalSpacing}`);
            }
            if (renderColHasFlooringTypeBehindIsland) {
              console.log(`🔵🔵🔵 COLUMN FLOORING_TYPE_BEHIND_ISLAND: after colY=${colY}, spacing added: ${rowHeight + renderColTotalSpacing}, totalSpacing=${renderColTotalSpacing}`);
            }
          }
          
          // If this is the last row and section continued, draw side borders
          if (showSectionBorders && sectionContinues && rowIndex === fieldRows.length - 1) {
            page.drawLine({
              start: { x: colX, y: continuedSectionStartY },
              end: { x: colX, y: colY },
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
            page.drawLine({
              start: { x: colX + columnWidth, y: continuedSectionStartY },
              end: { x: colX + columnWidth, y: colY },
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
            // Draw bottom border
            page.drawLine({
              start: { x: colX, y: colY },
              end: { x: colX + columnWidth, y: colY },
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
          }
        }

        columnYPositions[colNum] = colY;
      }
    }

    // Find the lowest Y position across all columns
    // Note: columnYPositions includes the final spacing after the last field
    // We need to add back the field spacing so the border extends to where content actually ends
    const rawMinY = Math.min(...Object.values(columnYPositions));
    // Add back the field spacing to get the actual content end position
    // This ensures the border extends to where the last field ends, not including trailing spacing
    // fieldSpacing is already defined in the function parameters, so we use it directly
    // Add 1 point to extend slightly lower for better visual alignment
    const minY = rawMinY + fieldSpacing - 10;

    if (showSectionBorders) {
      // Extend the last section's border in each column down to minY to match FormRenderer behavior
      // This ensures the last section in each column extends its border to fill the remaining space
      console.log(`🔵 BORDER EXTENSION START: rawMinY=${rawMinY}, minY=${minY} (rawMinY + fieldSpacing), columnYPositions=`, columnYPositions);
      console.log(`🔵 BORDER EXTENSION: lastSectionBorders=`, lastSectionBorders);
      console.log(`🔵 BORDER EXTENSION: columnWidth=${columnWidth}, columnNumbers=`, columnNumbers);
      
      for (const colNum of columnNumbers) {
        const lastBorder = lastSectionBorders[colNum];
        const finalColY = columnYPositions[colNum];
        
        console.log(`🔵 BORDER EXTENSION column ${colNum}: lastBorder=`, lastBorder, 'finalColY=', finalColY);
        
        if (lastBorder) {
          // Determine where the actual bottom of content is (use the lower of sectionBottomY and finalColY)
          // Since Y decreases downward, the lower value is the one that's further down on the page
          const actualBottomY = Math.min(lastBorder.sectionBottomY, finalColY);
          const extensionDistance = actualBottomY - minY;
          
          console.log(`🔵 BORDER EXTENSION column ${colNum}: sectionTopY=${lastBorder.sectionTopY}, sectionBottomY=${lastBorder.sectionBottomY}, finalColY=${finalColY}`);
          console.log(`🔵 BORDER EXTENSION column ${colNum}: actualBottomY=${actualBottomY}, minY=${minY}, extensionDistance=${extensionDistance}`);
          console.log(`🔵 BORDER EXTENSION column ${colNum}: colX=${lastBorder.colX}, columnWidth=${columnWidth}`);
          
          // Only extend if the actual bottom is above minY (i.e., this column is shorter than the tallest)
          if (actualBottomY > minY) {
            console.log(`🔵 BORDER EXTENSION column ${colNum}: EXTENDING from ${actualBottomY} to ${minY} (distance: ${extensionDistance}pt)`);
            
            // The rectangle's side borders end exactly at sectionBottomY (the bottom edge of the rectangle)
            // To eliminate gaps, we need to ensure our extension lines connect seamlessly
            // Since lines with thickness 1 are centered on their coordinates (extending ±0.5pt),
            // and the rectangle's side borders end at sectionBottomY, we need to start the extension
            // slightly above sectionBottomY to ensure there's no gap
            // Start extension at sectionBottomY - 0.5 to overlap with the rectangle border
            const extensionStartY = lastBorder.sectionBottomY - 0.5;
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: extensionStartY=${extensionStartY} (sectionBottomY=${lastBorder.sectionBottomY})`);
            
            // Erase the original bottom border of the rectangle by drawing white over it
            // The original rectangle bottom border was drawn at sectionBottomY
            // With borderWidth 1, the line extends ±0.5pt from the coordinate
            // We need to erase just the horizontal bottom border line, not the side borders
            // Start further inside the left border to avoid erasing the corner connection
            // End slightly before the right border to avoid erasing the corner connection
            const leftBorderOverlap = 1.0; // More overlap on left to ensure it doesn't affect left border
            const rightBorderOverlap = 0.5; // Less overlap on right (already working)
            const eraseX = lastBorder.colX + leftBorderOverlap;
            const eraseY = lastBorder.sectionBottomY - 1.5;
            const eraseWidth = columnWidth - leftBorderOverlap - rightBorderOverlap; // Don't extend to edges
            const eraseHeight = 3;
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: Erasing bottom border with white rect: x=${eraseX}, y=${eraseY}, width=${eraseWidth}, height=${eraseHeight}`);
            
            page.drawRectangle({
              x: eraseX,
              y: eraseY,
              width: eraseWidth,
              height: eraseHeight,
              color: rgb(1, 1, 1),
              borderWidth: 0,
            });
            
            // Draw extended right side border starting slightly above sectionBottomY to ensure overlap
            // This eliminates any gap between the rectangle border and the extension
            const rightBorderStart = { x: lastBorder.colX + columnWidth, y: extensionStartY };
            const rightBorderEnd = { x: lastBorder.colX + columnWidth, y: minY };
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: Drawing right side border: from (${rightBorderStart.x}, ${rightBorderStart.y}) to (${rightBorderEnd.x}, ${rightBorderEnd.y})`);
            
            page.drawLine({
              start: rightBorderStart,
              end: rightBorderEnd,
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
            
            // Draw extended left side border starting slightly above sectionBottomY to ensure overlap
            // This eliminates any gap between the rectangle border and the extension
            const leftBorderStart = { x: lastBorder.colX, y: extensionStartY };
            const leftBorderEnd = { x: lastBorder.colX, y: minY };
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: Drawing left side border: from (${leftBorderStart.x}, ${leftBorderStart.y}) to (${leftBorderEnd.x}, ${leftBorderEnd.y})`);
            
            page.drawLine({
              start: leftBorderStart,
              end: leftBorderEnd,
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
            
            // Draw new bottom border at minY
            const bottomBorderStart = { x: lastBorder.colX, y: minY };
            const bottomBorderEnd = { x: lastBorder.colX + columnWidth, y: minY };
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: Drawing bottom border: from (${bottomBorderStart.x}, ${bottomBorderStart.y}) to (${bottomBorderEnd.x}, ${bottomBorderEnd.y})`);
            
            page.drawLine({
              start: bottomBorderStart,
              end: bottomBorderEnd,
              thickness: 1,
              color: rgb(0.7, 0.7, 0.7),
            });
            
            console.log(`🔵 BORDER EXTENSION column ${colNum}: Border extension COMPLETE`);
          } else {
            console.log(`🔵 BORDER EXTENSION column ${colNum}: No extension needed (actualBottomY=${actualBottomY} <= minY=${minY})`);
          }
        } else {
          console.log(`🔵 BORDER EXTENSION column ${colNum}: No lastBorder found`);
        }
      }
      
      console.log(`🔵 BORDER EXTENSION END`);
    }

    return { page, y: minY };
  }

  /**
   * Renders a dynamic header based on configuration
   * @returns The actual height of the rendered header
   */
  private static async renderDynamicHeader(
    page: PDFPage,
    pdfDoc: PDFDocument,
    headerConfig: HeaderConfiguration,
    pageWidth: number,
    pageHeight: number,
    pageMargin: number,
    headerHeight: number,
    font: PDFFont,
    boldFont: PDFFont,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {}
  ): Promise<number> {
    const actualHeaderHeight = headerConfig.height || headerHeight;
    const headerTopY = pageHeight - pageMargin;
    const headerBottomY = headerTopY - actualHeaderHeight;
    
    console.log('renderDynamicHeader: actualHeaderHeight:', actualHeaderHeight);
    console.log('renderDynamicHeader: headerTopY:', headerTopY, 'headerBottomY:', headerBottomY);
    console.log('renderDynamicHeader: backgroundColor:', headerConfig.backgroundColor);
    console.log('renderDynamicHeader: elements count:', headerConfig.elements?.length);
    
    // Draw background if specified
    if (headerConfig.backgroundColor) {
      const bgColor = this.parseColor(headerConfig.backgroundColor);
      console.log('renderDynamicHeader: Drawing background rectangle, color:', bgColor);
      page.drawRectangle({
        x: pageMargin,
        y: headerBottomY,
        width: pageWidth - (pageMargin * 2),
        height: actualHeaderHeight,
        color: bgColor,
        borderWidth: 0,
      });
    }
    
    // Helper function to replace placeholders in content (e.g., {{DisplayName}})
    const replacePlaceholders = (content: string): string => {
      return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        const value = headerData[key];
        return value !== undefined && value !== null ? String(value) : match;
      });
    };
    
    // Helper function to calculate X position
    const calculateX = (x: 'center' | 'left' | 'right' | 'margin' | number, contentWidth?: number): number => {
      if (typeof x === 'number') {
        return pageMargin + x;
      }
      switch (x) {
        case 'center':
          return contentWidth ? (pageWidth - contentWidth) / 2 : pageWidth / 2;
        case 'left':
          return pageMargin;
        case 'right': {
          const rightPadding = headerConfig.rightPadding || 0;
          return contentWidth 
            ? pageWidth - pageMargin - rightPadding - contentWidth 
            : pageWidth - pageMargin - rightPadding;
        }
        case 'margin':
          return pageMargin;
        default:
          return pageMargin;
      }
    };
    
    // Helper function to calculate X2 position for lines
    const calculateX2 = (x: 'margin' | 'page-width-minus-margin' | number): number => {
      if (typeof x === 'number') {
        return pageMargin + x;
      }
      switch (x) {
        case 'margin':
          return pageMargin;
        case 'page-width-minus-margin':
          return pageWidth - pageMargin;
        default:
          return pageWidth - pageMargin;
      }
    };
    
    // Render each element
    for (const element of headerConfig.elements) {
      console.log('renderDynamicHeader: Rendering element:', element.type, element);
      switch (element.type) {
        case 'text': {
          const content = replacePlaceholders(element.content);
          const elementFont = element.fontWeight === 'bold' ? boldFont : font;
          const fontSize = element.fontSize;
          const textWidth = elementFont.widthOfTextAtSize(content, fontSize);
          const textColor = this.parseColor(element.color);
          
          // Calculate X position
          let xPos = calculateX(element.x, textWidth);
          
          // If alignment is specified, adjust X position only if x is a string keyword
          // If x is a number, preserve it as an offset from the margin
          if (element.alignment && typeof element.x !== 'number') {
            if (element.alignment === 'left') {
              xPos = calculateX('left');
            } else if (element.alignment === 'right') {
              xPos = calculateX('right', textWidth);
            } else if (element.alignment === 'center') {
              xPos = calculateX('center', textWidth);
            }
          }
          
          // Calculate Y position (from top of header)
          const yPos = headerTopY - element.y - fontSize;
          
          console.log('renderDynamicHeader: Drawing text:', content, 'at x:', xPos, 'y:', yPos, 'fontSize:', fontSize, 'color:', textColor);
          
          // Draw text
          page.drawText(content, {
            x: xPos,
            y: yPos,
            size: fontSize,
            font: elementFont,
            color: textColor,
          });
          
          break;
        }
        
        case 'line': {
          const x1 = calculateX2(element.x1);
          const y1 = headerTopY - element.y1;
          const x2 = calculateX2(element.x2);
          const y2 = headerTopY - element.y2;
          const lineColor = this.parseColor(element.color);
          
          page.drawLine({
            start: { x: x1, y: y1 },
            end: { x: x2, y: y2 },
            thickness: element.thickness,
            color: lineColor,
          });
          
          break;
        }
        
        case 'rectangle': {
          const x = pageMargin + element.x;
          const y = headerTopY - element.y - element.height;
          const fillColor = element.fillColor ? this.parseColor(element.fillColor) : undefined;
          const borderColor = element.borderColor ? this.parseColor(element.borderColor) : undefined;
          
          // Calculate width - support string values like 'page-width-minus-margin'
          let width: number;
          if (typeof element.width === 'string') {
            if (element.width === 'page-width-minus-margin') {
              width = pageWidth - (pageMargin * 2);
            } else {
              width = element.width as any; // Fallback (shouldn't happen)
            }
          } else {
            width = element.width;
          }
          
          page.drawRectangle({
            x,
            y,
            width,
            height: element.height,
            color: fillColor,
            borderColor: borderColor,
            borderWidth: element.borderWidth || 0,
          });
          
          break;
        }
      }
    }
    
    return actualHeaderHeight;
  }
  
  /**
   * Parses a color string (hex, rgb, or named color) to RGB values
   */
  private static parseColor(colorString: string): ReturnType<typeof rgb> {
    // Hex color (#RRGGBB or #RGB)
    if (colorString.startsWith('#')) {
      const hex = colorString.slice(1);
      const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16) / 255;
      return rgb(r, g, b);
    }
    
    // RGB color (rgb(r, g, b))
    const rgbMatch = colorString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]) / 255;
      const g = parseInt(rgbMatch[2]) / 255;
      const b = parseInt(rgbMatch[3]) / 255;
      return rgb(r, g, b);
    }
    
    // Named colors (basic set)
    const namedColors: { [key: string]: ReturnType<typeof rgb> } = {
      black: rgb(0, 0, 0),
      white: rgb(1, 1, 1),
      red: rgb(1, 0, 0),
      green: rgb(0, 1, 0),
      blue: rgb(0, 0, 1),
      gray: rgb(0.5, 0.5, 0.5),
      grey: rgb(0.5, 0.5, 0.5),
    };
    
    const lowerColor = colorString.toLowerCase();
    if (namedColors[lowerColor]) {
      return namedColors[lowerColor];
    }
    
    // Default to black if color can't be parsed
    return rgb(0, 0, 0);
  }

  /**
   * Adds header to a page (dynamic header or image)
   */
  private static async addHeaderToPage(
    page: PDFPage,
    pdfDoc: PDFDocument,
    headerConfigurationJson: string | undefined,
    pageWidth: number,
    pageHeight: number,
    pageMargin: number,
    headerHeight: number,
    font: PDFFont,
    boldFont: PDFFont,
    headerData: { [key: string]: string | number | boolean | null | undefined } = {}
  ): Promise<number> {
    // Prefer dynamic header configuration, fallback to image
    if (headerConfigurationJson) {
      try {
        const headerConfig = JSON.parse(headerConfigurationJson) as HeaderConfiguration;
        if (headerConfig.type === 'dynamic' && headerConfig.elements) {
          return await this.renderDynamicHeader(
            page,
            pdfDoc,
            headerConfig,
            pageWidth,
            pageHeight,
            pageMargin,
            headerHeight || headerConfig.height || 100,
            font,
            boldFont,
            headerData
          );
        }
      } catch (error) {
        console.warn('Could not parse HeaderConfigurationJson for new page, falling back to image:', error);
      }
    }

    // No header image functionality - return 0
    return 0;
  }

  /**
   * Calculates the maximum label width needed for a set of fields
   */
  private static calculateMaxLabelWidth(fields: any[], availableWidth: number, font: PDFFont): number {
    let maxLabelWidth = 0;
    const labelSize = 8;
    
    for (const field of fields) {
      const fieldLabel = field.description || '';
      if (fieldLabel) {
        const labelText = fieldLabel + ':';
        const textWidth = font.widthOfTextAtSize(labelText, labelSize);
        maxLabelWidth = Math.max(maxLabelWidth, textWidth);
      }
    }
    
    // Use a reasonable maximum (min of calculated max, 100pt, or 40% of width)
    return Math.min(Math.max(maxLabelWidth + 5, 50), 100, availableWidth * 0.4);
  }

  /**
   * Calculates the height needed for a field label (accounts for wrapping)
   */
  private static calculateLabelHeight(field: any, maxLabelWidth: number, font: PDFFont): number {
    const fieldLabel = field.description || '';
    if (!fieldLabel) return 0;
    
    const labelSize = 8;
    const lineHeight = 10; // Height per line when wrapping
    const displayLabel = fieldLabel + ':';
    const labelTextWidth = font.widthOfTextAtSize(displayLabel, labelSize);
    
    // If label fits in one line, return single line height
    if (labelTextWidth <= maxLabelWidth) {
      return labelSize; // Single line uses just the font size
    }
    
    // Calculate number of lines needed
    const words = displayLabel.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      if (font.widthOfTextAtSize(testLine, labelSize) <= maxLabelWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);
    
    // Return height needed for all lines (first line is at y, subsequent lines below)
    return labelSize + ((lines.length - 1) * lineHeight);
  }

  /**
   * Calculates the number of wrapped lines for a checkbox list item label
   */
  private static calculateCheckboxListItemLines(labelText: string, maxLabelWidth: number, font: PDFFont, labelFontSize: number): number {
    const labelTextWidth = font.widthOfTextAtSize(labelText, labelFontSize);
    
    if (labelTextWidth <= maxLabelWidth) {
      return 1; // Single line
    }

    return this.wrapText(labelText, maxLabelWidth, font, labelFontSize).length;
  }

  /**
   * Adds an editable field to the PDF using pdf-lib
   */
  private static async addEditableFieldToPDF(
    page: PDFPage,
    form: PDFForm,
    field: any,
    formData: any,
    x: number,
    y: number,
    width: number,
    height: number,
    font: PDFFont,
    labelWidth?: number, // Optional: use consistent label width for alignment
    labelFieldGapOverride?: number, // Optional: override the gap between label and input (default 3)
    defaultFontSize?: number, // Optional: default font size for text fields (default: 7)
    labelFontSize?: number, // Optional: font size for labels (default: 8)
    multilineFontSize?: number, // Optional: font size for multiline fields (default: 7)
    checkboxListColumns?: number // Optional: number of columns for checkbox lists (default: 3)
  ): Promise<void> {
    const fieldLabel = field.description || ''; // Use empty string instead of field.code as fallback
    // Parse field configuration first to access defaultValue
    const config = PDFService.parseFieldConfiguration(field);

    // Get field value - priority: formData > defaultValue > empty
    // For checkbox lists it's an array, for others it's a string/primitive
    // Preserve false values for boolean fields (don't default to empty string)
    const rawValue = formData[field.code];
    const fieldValue = rawValue !== undefined && rawValue !== null ? rawValue :
      (config.defaultValue !== undefined && config.defaultValue !== null ? config.defaultValue : '');
    const fieldName = field.code;
    const fieldFontSize = config.fontSize ?? defaultFontSize ?? 7;
    const fieldLabelFontSize = config.labelFontSize ?? labelFontSize ?? 8;
    const fieldTextAlign = config.textAlign || 'left';
    const fieldLabelTextAlign = formService.getContextValue(config, 'labelTextAlign', 'pdf') || 'left';
    const fieldSuffix = config.suffix || '';
    const fieldPrefix = config.prefix || '';
    const fieldMultilineLines = config.multilineLines ?? 3;
    const fieldOnNextLine = config.fieldOnNextLine || false;
    const displayAsLabel = config.displayAsLabel || false;
    const labelAfterField = config.labelAfterField || false;
    
    // Calculate affix widths to reserve space
    const suffixWidth = fieldSuffix ? font.widthOfTextAtSize(fieldSuffix, fieldFontSize) + 3 : 0; // +3 for spacing
    const prefixWidth = fieldPrefix ? font.widthOfTextAtSize(fieldPrefix, fieldFontSize) + 3 : 0; // +3 for spacing
    
    const formatWithAffixes = (rawValue: any): string => {
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return '';
      }
      const baseValue = rawValue.toString();
      const withPrefix = fieldPrefix ? `${fieldPrefix}${baseValue}` : baseValue;
      return fieldSuffix ? `${withPrefix}${fieldSuffix}` : withPrefix;
    };
    const drawAffixText = (affix: string, position: 'prefix' | 'suffix', inputFieldX: number, inputFieldWidth: number) => {
      if (!affix) {
        return;
      }
      const textY = y - height + 12;
      const textOptions = {
        y: textY,
        size: fieldFontSize,
        font: font,
        color: rgb(0, 0, 0),
      } as const;

      if (position === 'suffix') {
        page.drawText(affix, {
          x: inputFieldX + inputFieldWidth + 3,
          ...textOptions,
        });
      } else {
        page.drawText(affix, {
          x: inputFieldX - font.widthOfTextAtSize(affix, fieldFontSize) - 3,
          ...textOptions,
        });
      }
    };
    
    // Use provided labelWidth or calculate it (for backward compatibility)
    // If labelWidth is provided, always use it (even for empty labels) to maintain alignment
    // For fieldOnNextLine, don't reserve side space for label
    // Check for skipLabelArea configuration to skip label area reservation
    const skipLabelArea = config.skipLabelArea || false;
    const actualLabelWidth = (fieldOnNextLine && fieldLabel) ? 0 : (skipLabelArea ? 0 : (labelWidth !== undefined 
      ? labelWidth  // Always use provided labelWidth for alignment, even if no label
      : (fieldLabel ? Math.min(120, width * 0.4) : 0)));
    
    // Reduce spacing between label and input for better alignment
    // If labelWidth is provided, always reserve the label area for alignment (unless skipLabelArea is true)
    const labelFieldGap = labelFieldGapOverride !== undefined ? labelFieldGapOverride : 3; // Use override or default to 3
    const shouldReserveLabelArea = skipLabelArea ? false : ((fieldOnNextLine && fieldLabel) ? false : (labelWidth !== undefined || (fieldLabel && !labelAfterField)));
    const fieldX = shouldReserveLabelArea ? x + actualLabelWidth + labelFieldGap : x;

    // Calculate available width for field, reserving space for affixes
    const baseFieldWidth = shouldReserveLabelArea ? width - actualLabelWidth - labelFieldGap : width;
    const actualFieldWidth = baseFieldWidth - suffixWidth - prefixWidth;

    // Helper function to calculate label x position based on alignment
    const calculateLabelX = (labelText: string, labelSize: number, availableWidth: number): number => {
      const textWidth = font.widthOfTextAtSize(labelText, labelSize);
      switch (fieldLabelTextAlign) {
        case 'center':
          return x + (availableWidth / 2) - (textWidth / 2);
        case 'right':
          return x + availableWidth - textWidth;
        case 'left':
        default:
          return x;
      }
    };

    // Draw field label only if it's not empty
    // For fieldOnNextLine, draw label above the field
    if (fieldLabel && fieldOnNextLine) {
      const labelSize = fieldLabelFontSize;
      const displayLabel = fieldLabel + ':';
      const labelX = calculateLabelX(displayLabel, labelSize, width);
      // Draw label at calculated x position, above the field (y + label height + spacing)
      page.drawText(displayLabel, {
        x: labelX,
        y: y + 10, // Position label above the field
        size: labelSize,
        font: font,
        color: rgb(0, 0, 0),
      });
      // Adjust y position for the field to be below the label
      // This will be used for multiline fields below
    } else if (fieldLabel && labelAfterField) {
      // Label positioned after the field (to the right)
      const displayLabel = ':' + fieldLabel;
      const labelSize = fieldLabelFontSize;
      const labelX = fieldX + actualFieldWidth + labelFieldGap;
      const labelY = y - height + 12; // Match field text baseline position

      page.drawText(displayLabel, {
        x: labelX,
        y: labelY,
        size: labelSize,
        font: font,
        color: rgb(0, 0, 0),
      });
    } else if (fieldLabel) {
      const maxLabelWidth = actualLabelWidth - 5;
      let displayLabel = fieldLabel + ':';
      let labelSize = fieldLabelFontSize; // Use field-specific or configured label font size

      // Check if label needs to wrap to multiple lines
      const labelTextWidth = font.widthOfTextAtSize(displayLabel, labelSize);

      if (labelTextWidth > maxLabelWidth) {
        // Split label into multiple lines
        const words = displayLabel.split(' ');
        const lines: string[] = [];
        let currentLine = '';
        
        for (const word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          if (font.widthOfTextAtSize(testLine, labelSize) <= maxLabelWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        
        // Draw multiple lines with proper spacing
        // Align first line with field text baseline, subsequent lines below
        const firstLineY = y - height + 12; // Match field text baseline position
        const lineHeight = labelSize + 2; // Line height based on font size
        lines.forEach((line, index) => {
          const lineX = calculateLabelX(line, labelSize, actualLabelWidth);
          page.drawText(line, {
            x: lineX,
            y: firstLineY - (index * lineHeight), // First line at field text baseline, subsequent lines below (subtract in PDF coordinates)
            size: labelSize,
            font: font,
            color: rgb(0, 0, 0),
          });
        });
      } else {
        // Single line label
        // Align label text baseline with field text baseline
        // Field text is at y - height + 12, so labels should match that
        const labelX = calculateLabelX(displayLabel, labelSize, actualLabelWidth);
        const labelY = y - height + 12; // Match field text baseline position
        page.drawText(displayLabel, {
          x: labelX,
          y: labelY,
          size: labelSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
    }

    // Normalize fieldType to handle both string and numeric enum values
    const normalizedFieldType = this.normalizeFieldType(field.fieldType);
    
    // Debug logging for all fields to see what's being processed
    console.log(`PDF Processing field: ${field.code} (${field.description}), fieldType: ${field.fieldType} (${typeof field.fieldType}), normalized: ${normalizedFieldType}`);
    if (fieldPrefix || fieldSuffix) {
      console.log('PDF field affix metadata:', {
        code: field.code,
        type: normalizedFieldType,
        prefix: fieldPrefix,
        suffix: fieldSuffix,
        rawValue: fieldValue
      });
    }
    
    // If displayAsLabel is true, render the field value as text instead of an editable field
    if (displayAsLabel) {
      const displayValue = formatWithAffixes(fieldValue);
      console.log(`PDF displayAsLabel for ${field.code}: fieldValue=`, fieldValue, 'displayValue=', displayValue, 'fieldOnNextLine=', fieldOnNextLine, 'rawValue from formData=', rawValue);
      
      const valueFontSize = fieldFontSize;
      const valueX = fieldOnNextLine ? x : fieldX;
      const valueWidth = fieldOnNextLine ? width : actualFieldWidth;
      
      // For multiline fields, handle line wrapping
      if (normalizedFieldType === 'Multiline') {
        const displayMultilineFontSize = config.fontSize ?? multilineFontSize ?? 7;
        const maxWidth = valueWidth;
        const lineHeight = displayMultilineFontSize + 2;
        
        // Handle multiline text - split by newlines first, then wrap each line if needed
        const textToDisplay = displayValue || '';
        const paragraphs = textToDisplay.split('\n');
        const lines: string[] = [];
        
        paragraphs.forEach(paragraph => {
          if (!paragraph.trim()) {
            lines.push(''); // Empty line
            return;
          }
          
          const words = paragraph.split(' ');
          let currentLine = '';
          
          for (const word of words) {
            const testLine = currentLine + (currentLine ? ' ' : '') + word;
            if (font.widthOfTextAtSize(testLine, displayMultilineFontSize) <= maxWidth) {
              currentLine = testLine;
            } else {
              if (currentLine) lines.push(currentLine);
              currentLine = word;
            }
          }
          if (currentLine) lines.push(currentLine);
        });
        
        // Calculate starting Y position - if fieldOnNextLine, position below label (label is at y + 10)
        const startY = fieldOnNextLine ? y - 2 : y;
        
        console.log(`PDF displayAsLabel multiline for ${field.code}: lines=`, lines, 'startY=', startY, 'valueX=', valueX, 'valueWidth=', valueWidth);
        
        // Draw multiline text - only draw if there are actual lines with content
        if (lines.length > 0 && lines.some(line => line.trim().length > 0)) {
          lines.forEach((line, index) => {
            // Skip empty lines
            if (!line || !line.trim()) {
              return;
            }
            
            let lineX = valueX;
            const lineWidth = font.widthOfTextAtSize(line, displayMultilineFontSize);
            switch (fieldTextAlign) {
              case 'center':
                lineX = valueX + (valueWidth / 2) - (lineWidth / 2);
                break;
              case 'right':
                lineX = valueX + valueWidth - lineWidth;
                break;
              case 'left':
              default:
                lineX = valueX;
                break;
            }
            
            // Calculate Y position - for fieldOnNextLine, label is at y + 10, so value starts at y - 2
            // For normal fields, value starts at y
            const lineY = startY - (index * lineHeight);
            
            page.drawText(line, {
              x: lineX,
              y: lineY,
              size: displayMultilineFontSize,
              font: font,
              color: rgb(0, 0, 0),
            });
          });
        } else {
          console.log(`PDF displayAsLabel: Skipping empty multiline field ${field.code} - no content to display`);
        }
      } else if (displayValue) {
        // Single line text
        const textWidth = font.widthOfTextAtSize(displayValue, valueFontSize);
        let textX = valueX;
        switch (fieldTextAlign) {
          case 'center':
            textX = valueX + (valueWidth / 2) - (textWidth / 2);
            break;
          case 'right':
            textX = valueX + valueWidth - textWidth;
            break;
          case 'left':
          default:
            textX = valueX;
            break;
        }
        
        // If fieldOnNextLine, position below label; otherwise at normal field position
        const textY = fieldOnNextLine ? y - 2 : (y - height + 12);
        console.log(`PDF displayAsLabel single line for ${field.code}: textX=`, textX, 'textY=', textY, 'displayValue=', displayValue);
        page.drawText(displayValue, {
          x: textX,
          y: textY,
          size: valueFontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
      
      // Return early - don't create editable field
      return;
    }
    
    // Add field based on type
    switch (normalizedFieldType) {
      case 'String':
      case 'Int':
        // Create text field (or get existing if already created)
        let textField;
        try {
          textField = form.createTextField(fieldName);
        } catch (error: any) {
          // Field already exists, get it instead
          if (error.message?.includes('already exists')) {
            textField = form.getTextField(fieldName);
          } else {
            throw error;
          }
        }
        const textValue = formatWithAffixes(fieldValue);
        if (fieldPrefix || fieldSuffix) {
          console.log('PDF formatted value:', {
            code: field.code,
            type: normalizedFieldType,
            rawValue: fieldValue,
            formattedValue: textValue
          });
        }
        textField.setText(textValue);
        // Calculate input field position and width (accounting for prefix space)
        const inputFieldX = fieldX + prefixWidth;
        const inputFieldWidth = actualFieldWidth;
        
        textField.addToPage(page, {
          x: inputFieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: inputFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        if (!fieldValue && fieldSuffix) {
          drawAffixText(fieldSuffix, 'suffix', inputFieldX, inputFieldWidth);
        }
        if (!fieldValue && fieldPrefix) {
          drawAffixText(fieldPrefix, 'prefix', inputFieldX, inputFieldWidth);
        }
        try {
          textField.setFontSize(fieldFontSize);
        } catch (e) {
          console.warn('Could not set font size for text field:', e);
        }
        break;

      case 'Dimension':
        console.log(`🔵 DIMENSION FIELD CASE HIT: ${field.code}`);
        console.log(`🔵 DIMENSION field ${field.code}: fieldValue=`, fieldValue, 'type:', typeof fieldValue);
        console.log(`🔵 DIMENSION field ${field.code}: fieldLabel=`, fieldLabel);
        console.log(`🔵 DIMENSION field ${field.code}: x=`, x, 'width=', width, 'fieldX=', fieldX, 'actualFieldWidth=', actualFieldWidth);
        
        // Parse dimension value (format: "H:value W:value D:value")
        const parseDimensionValue = (val: string) => {
          if (!val) return { height: '', width: '', depth: '' };
          const parts = val.split(' ');
          const result = { height: '', width: '', depth: '' };
          parts.forEach(part => {
            if (part.startsWith('H:')) result.height = part.substring(2);
            else if (part.startsWith('W:')) result.width = part.substring(2);
            else if (part.startsWith('D:')) result.depth = part.substring(2);
          });
          return result;
        };
        
        const dimensionValue = parseDimensionValue(fieldValue || '');
        console.log(`🔵 DIMENSION parsed values:`, dimensionValue);
        
        // For dimension fields, respect label area alignment (even if no main label)
        // Use fieldX and actualFieldWidth to align with other fields in the section
        const dimensionFieldsX = fieldX;
        const dimensionFieldsWidth = actualFieldWidth;
        
        console.log(`🔵 DIMENSION layout: dimensionFieldsX=`, dimensionFieldsX, 'dimensionFieldsWidth=', dimensionFieldsWidth);
        
        // Create three text fields for H, W, D side by side
        // Divide available width into 3 fields with small gaps between
        const dimensionFieldGap = 5; // Gap between dimension fields
        const dimensionFieldWidth = (dimensionFieldsWidth - (dimensionFieldGap * 2)) / 3;
        const dimensionLabels = ['H', 'W', 'D'];
        const dimensionValues = [dimensionValue.height, dimensionValue.width, dimensionValue.depth];
        
        console.log(`🔵 DIMENSION fieldWidth=`, dimensionFieldWidth, 'values=', dimensionValues);
        
        // Y position for dimension fields - same line as label (like checkbox lists)
        // Label is drawn at y - height + 12, so dimension fields should be at the same Y
        const dimensionFieldsY = y; // Use same Y as label, fields will align with label baseline
        
        console.log(`🔵 DIMENSION creating 3 fields at Y=`, dimensionFieldsY);
        
        for (let i = 0; i < 3; i++) {
          const dimFieldX = dimensionFieldsX + (i * (dimensionFieldWidth + dimensionFieldGap));
          const dimFieldName = `${fieldName}_${dimensionLabels[i]}`;

          console.log(`🔵 DIMENSION creating field ${i} (${dimensionLabels[i]}): name=`, dimFieldName, 'x=', dimFieldX, 'y=', dimensionFieldsY, 'width=', dimensionFieldWidth - 15, 'value=', dimensionValues[i]);

          // Create text field for this dimension with suffix
          const dimTextField = form.createTextField(dimFieldName);
          dimTextField.setText(dimensionValues[i] || '');
          dimTextField.addToPage(page, {
            x: dimFieldX,
            y: dimensionFieldsY - height + 8, // Adjust for label baseline (ascender)
            width: dimensionFieldWidth - 15, // Leave space for suffix
            height: height,
            borderWidth: 1,
            borderColor: rgb(0.7, 0.7, 0.7),
          });
          try {
            dimTextField.setFontSize(defaultFontSize ?? 7); // Use configured font size or default to 7
          } catch (e) {
            console.warn('Could not set font size for dimension field:', e);
          }

          // Draw suffix for this dimension (H, W, D)
          const suffixX = dimFieldX + (dimensionFieldWidth - 15) + 3; // Position after the field with small gap
          const suffixY = dimensionFieldsY - height + 12; // Match field text baseline
          page.drawText(dimensionLabels[i], {
            x: suffixX,
            y: suffixY,
            size: defaultFontSize ?? 7,
            font: font,
            color: rgb(0, 0, 0),
          });

          console.log(`🔵 DIMENSION field ${i} (${dimensionLabels[i]}) created successfully`);
        }
        console.log(`🔵 DIMENSION field ${field.code} complete - all 3 fields created`);
        break;

      case 'Multiline':
        // Create multiline text field (or get existing if already created)
        // Height calculated dynamically based on font size and number of lines
        const customMultilineFontSize = config.fontSize ?? multilineFontSize ?? 7;
        const customMultilineHeight = this.calculateCustomMultilineHeight(customMultilineFontSize, fieldMultilineLines);
        let multilineField;
        try {
          multilineField = form.createTextField(fieldName);
        } catch (error: any) {
          // Field already exists, get it instead
          if (error.message?.includes('already exists')) {
            multilineField = form.getTextField(fieldName);
          } else {
            throw error;
          }
        }
        multilineField.setText(fieldValue ? fieldValue.toString() : '');
        multilineField.enableMultiline();
        
        // For fieldOnNextLine, use full width and position field below label
        const multilineFieldX = fieldOnNextLine ? x : fieldX;
        const multilineFieldWidth = fieldOnNextLine ? width : actualFieldWidth;
        // When field is on next line, label is drawn at y + 10, so field should be at y - 2 (below label)
        // Standard position: y - customMultilineHeight + 8
        const multilineFieldY = fieldOnNextLine 
          ? y - 2  // Position below label (label is at y + 10, field starts at y - 2)
          : y - customMultilineHeight + 8; // Standard position
        
        multilineField.addToPage(page, {
          x: multilineFieldX,
          y: multilineFieldY,
          width: multilineFieldWidth,
          height: customMultilineHeight,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        try {
          multilineField.setFontSize(customMultilineFontSize);
        } catch (e) {
          console.warn('Could not set font size for multiline field:', e);
        }
        break;

      case 'Bool':
        // Create checkbox
        console.log(`PDF Bool field ${field.code}: fieldValue=`, fieldValue, 'type:', typeof fieldValue);
        
        // Determine if checkbox should be checked
        // Handle boolean true, string "true", string "1", number 1, etc.
        let shouldCheck = false;
        if (typeof fieldValue === 'boolean') {
          shouldCheck = fieldValue === true;
        } else if (typeof fieldValue === 'string') {
          shouldCheck = fieldValue.toLowerCase() === 'true' || fieldValue === '1';
        } else if (typeof fieldValue === 'number') {
          shouldCheck = fieldValue === 1;
        } else {
          // For other types, use truthy check
          shouldCheck = !!fieldValue;
        }
        
        console.log(`PDF Bool field ${field.code}: shouldCheck=`, shouldCheck);

        let checkBox;
        try {
          checkBox = form.createCheckBox(fieldName);
        } catch (error: any) {
          // Field already exists, get it instead
          if (error.message?.includes('already exists')) {
            checkBox = form.getCheckBox(fieldName);
          } else {
            throw error;
          }
        }

        // Add checkbox to page first
        checkBox.addToPage(page, {
          x: fieldX,
          y: y - 13 + 8, // Align top with label baseline adjustment (10pt box, 8pt offset)
          width: 10,
          height: 10,
          borderWidth: 1,
          borderColor: rgb(0, 0, 0),
        });
        
        // Check the checkbox AFTER adding to page (pdf-lib requires this order)
        if (shouldCheck) {
          checkBox.check();
        }
        break;

      case 'Dropdown':
        // For dropdown, create a text field (pdf-lib doesn't support dropdowns easily)
        let dropdownField;
        try {
          dropdownField = form.createTextField(fieldName);
        } catch (error: any) {
          if (error.message?.includes('already exists')) {
            dropdownField = form.getTextField(fieldName);
          } else {
            throw error;
          }
        }
        const dropdownValue = formatWithAffixes(fieldValue);
        if (fieldPrefix || fieldSuffix) {
          console.log('PDF formatted value:', {
            code: field.code,
            type: normalizedFieldType,
            rawValue: fieldValue,
            formattedValue: dropdownValue
          });
        }
        dropdownField.setText(dropdownValue);
        // Calculate input field position and width (accounting for prefix space)
        const dropdownInputFieldX = fieldX + prefixWidth;
        const dropdownInputFieldWidth = actualFieldWidth;
        
        dropdownField.addToPage(page, {
          x: dropdownInputFieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: dropdownInputFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        if (!fieldValue && fieldSuffix) {
          drawAffixText(fieldSuffix, 'suffix', dropdownInputFieldX, dropdownInputFieldWidth);
        }
        if (!fieldValue && fieldPrefix) {
          drawAffixText(fieldPrefix, 'prefix', dropdownInputFieldX, dropdownInputFieldWidth);
        }
        try {
          dropdownField.setFontSize(defaultFontSize ?? 7); // Use configured font size or default to 7
        } catch (e) {
          console.warn('Could not set font size for dropdown field:', e);
        }
        break;

      case 'CheckboxList':
        // For checkbox list, create individual checkboxes for each option
        // Display them in 3 columns
        console.log(`🔵 PDF CheckboxList CASE HIT: ${field.code}`);
        console.log(`PDF CheckboxList field ${field.code}: fieldValue=`, fieldValue, 'type:', typeof fieldValue);
        
        const items = field.formFieldDataType?.formFieldDataItems || [];
        const activeItems = items
          .filter((item: any) => item.isActive !== false)
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        
        // Parse selected values
        let selectedCodes: string[] = [];
        console.log(`PDF CheckboxList field ${field.code}: parsing fieldValue=`, fieldValue, 'type:', typeof fieldValue);
        
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          try {
            // If fieldValue is already an array (from FormRenderer), use it directly
            if (Array.isArray(fieldValue)) {
              selectedCodes = fieldValue;
              console.log(`PDF CheckboxList field ${field.code}: fieldValue is array, selectedCodes=`, selectedCodes);
            } else if (typeof fieldValue === 'string') {
              // Try to parse as JSON first
              try {
                const parsed = JSON.parse(fieldValue);
                if (Array.isArray(parsed)) {
                  selectedCodes = parsed;
                  console.log(`PDF CheckboxList field ${field.code}: parsed JSON array, selectedCodes=`, selectedCodes);
                } else {
                  // If not an array, try comma-separated string
                  selectedCodes = fieldValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                  console.log(`PDF CheckboxList field ${field.code}: parsed as comma-separated string, selectedCodes=`, selectedCodes);
                }
              } catch {
                // If JSON parse fails, try comma-separated string
                selectedCodes = fieldValue.split(',').map((v: string) => v.trim()).filter((v: string) => v);
                console.log(`PDF CheckboxList field ${field.code}: JSON parse failed, using comma-separated, selectedCodes=`, selectedCodes);
              }
            } else {
              console.log(`PDF CheckboxList field ${field.code}: unexpected fieldValue type:`, typeof fieldValue);
            }
          } catch (e) {
            console.error(`PDF CheckboxList field ${field.code}: error parsing fieldValue`, e);
          }
        } else {
          console.log(`PDF CheckboxList field ${field.code}: fieldValue is empty/null/undefined`);
        }
        
        console.log(`PDF CheckboxList field ${field.code}: final selectedCodes=`, selectedCodes, 'activeItems=', activeItems.length);
        
        // Layout checkboxes in configurable columns
        const checkboxSize = 10; // Size of checkbox (matches regular checkboxes)
        const checkboxSpacing = 3; // Spacing between checkbox and label
        const rowSpacing = 10; // Vertical spacing between rows
        const numColumns = config.checkboxColumns ?? checkboxListColumns ?? 3; // Number of columns (field config overrides global)
        const columnGap = 10; // Horizontal gap between columns
        const checkboxLabelSpacing = 2; // Space between checkbox and its label text
        const labelFontSize = 8;
        
        // Calculate column widths (split available width into 3 columns, accounting for gaps)
        const columnWidth = (actualFieldWidth - (columnGap * (numColumns - 1))) / numColumns;
        const itemsPerColumn = Math.ceil(activeItems.length / numColumns);
        const maxLabelWidth = columnWidth - checkboxSize - checkboxLabelSpacing - 1;
        const lineSpacing = 2; // Spacing between wrapped lines
        
        // First pass: Calculate row heights based on wrapped labels
        // Group items by row and calculate the tallest label height for each row
        const rowHeights: number[] = []; // Store height needed for each row
        const numRows = Math.ceil(activeItems.length / numColumns);
        
        for (let rowIndex = 0; rowIndex < numRows; rowIndex++) {
          let maxRowHeight = checkboxSize; // Base height is checkbox size
          
          // Check all items in this row (items in different columns but same row)
          for (let colIndex = 0; colIndex < numColumns; colIndex++) {
            const itemIndex = rowIndex * numColumns + colIndex;
            if (itemIndex >= activeItems.length) break;
            
            const item = activeItems[itemIndex];
            const labelText = item.description;
            const labelTextWidth = font.widthOfTextAtSize(labelText, labelFontSize);
            
            // Calculate how many lines this label needs
            let numLines = 1;
            if (labelTextWidth > maxLabelWidth) {
              // Calculate number of lines needed
              const words = labelText.split(' ');
              const lines: string[] = [];
              let currentLine = '';
              
              for (const word of words) {
                const testLine = currentLine + (currentLine ? ' ' : '') + word;
                if (font.widthOfTextAtSize(testLine, labelFontSize) <= maxLabelWidth) {
                  currentLine = testLine;
                } else {
                  if (currentLine) lines.push(currentLine);
                  currentLine = word;
                }
              }
              if (currentLine) lines.push(currentLine);
              numLines = lines.length;
            }
            
            // Calculate height needed for this label (accounting for wrapped lines)
            const labelHeight = numLines === 1 
              ? labelFontSize // Single line
              : labelFontSize + ((numLines - 1) * (labelFontSize + lineSpacing)); // Wrapped lines
            
            // Row height is the maximum of checkbox height and label height, plus some spacing
            const itemHeight = Math.max(checkboxSize, labelHeight);
            maxRowHeight = Math.max(maxRowHeight, itemHeight);
          }
          
          // Add minimum spacing between rows
          rowHeights.push(maxRowHeight + PDFService.CHECKBOX_LIST_ROW_SPACING);
        }
        
        // Second pass: Render items using cumulative row heights
        // Calculate if the main label wraps (for positioning checkbox list below it)
        // The label is drawn first, so if it wraps, we need to start the checkbox list below it
        let mainLabelWraps = false;
        let mainLabelWrapHeight = 0;
        if (fieldLabel) {
          const maxLabelWidth = labelWidth !== undefined ? (labelWidth - 5) : (actualFieldWidth * 0.4);
          const labelTextWidth = font.widthOfTextAtSize(fieldLabel + ':', 8);
          if (labelTextWidth > maxLabelWidth) {
            // Calculate how many lines the main label needs (same logic as label drawing)
            const words = (fieldLabel + ':').split(' ');
            const lines: string[] = [];
            let currentLine = '';
            for (const word of words) {
              const testLine = currentLine + (currentLine ? ' ' : '') + word;
              if (font.widthOfTextAtSize(testLine, 8) <= maxLabelWidth) {
                currentLine = testLine;
              } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              }
            }
            if (currentLine) lines.push(currentLine);
            if (lines.length > 1) {
              mainLabelWraps = true;
              // Label draws at y - (index * 10), so for 2 lines: y and y - 10
              // Total height: 8pt (first line) + 10pt (spacing) + 8pt (second line) = 26pt
              // But we only need to account for the extra height beyond the first line
              mainLabelWrapHeight = (lines.length - 1) * 10; // Extra height for wrapped lines
            }
          }
        }
        
        // Start checkboxes below the label
        // The label's first line is drawn at firstLineY = y - height + 12
        // Checkboxes should align with the first line of the label (not the last line)
        // For wrapped labels, the additional lines are drawn in the label area (left side),
        // so checkboxes should start just below the first line, not below the last line
        // For both single and wrapped labels: start at firstLineY - 2 (2pt spacing after first line)
        const firstLineY = y - height + 12; // Label's first line baseline position
        let currentY = firstLineY - 2; // Always position relative to first line, with 2pt spacing
        
        // Create checkboxes for each item
        for (let i = 0; i < activeItems.length; i++) {
          const item = activeItems[i];
          const isSelected = selectedCodes.includes(item.code);
          
          // Distribute items across 3 columns
          const columnIndex = i % numColumns; // Column index (0, 1, or 2) - distribute evenly
          const rowIndex = Math.floor(i / numColumns); // Row index within the column
          
          // Calculate Y position using cumulative row heights
          let cumulativeHeight = 0;
          for (let r = 0; r < rowIndex; r++) {
            cumulativeHeight += rowHeights[r];
          }
          const checkboxYPos = currentY - cumulativeHeight;
          
          const checkboxX = fieldX + (columnIndex * (columnWidth + columnGap));
          
          // Create checkbox with unique name
          const checkboxFieldName = `${fieldName}_${item.code}`;
          const checkboxField = form.createCheckBox(checkboxFieldName);
          
          // Add checkbox to page first
          checkboxField.addToPage(page, {
            x: checkboxX,
            y: checkboxYPos,
            width: checkboxSize,
            height: checkboxSize,
            borderWidth: 1,
            borderColor: rgb(0, 0, 0),
          });
          
          // Check the checkbox AFTER adding to page (pdf-lib requires this order)
          if (isSelected) {
            checkboxField.check();
            console.log(`PDF CheckboxList: checked ${item.code} for field ${field.code}`);
          }
          
          // Draw label text next to checkbox with wrapping instead of ellipsis
          const labelX = checkboxX + checkboxSize + checkboxLabelSpacing;
          const labelY = checkboxYPos + checkboxSize / 2 - labelFontSize / 2;
          
          // Wrap label if too long for column width
          let labelText = item.description;
          const labelTextWidth = font.widthOfTextAtSize(labelText, labelFontSize);
          
          if (labelTextWidth > maxLabelWidth) {
            // Split label into multiple lines
            const words = labelText.split(' ');
            const lines: string[] = [];
            let currentLine = '';
            
            for (const word of words) {
              const testLine = currentLine + (currentLine ? ' ' : '') + word;
              if (font.widthOfTextAtSize(testLine, labelFontSize) <= maxLabelWidth) {
                currentLine = testLine;
              } else {
                if (currentLine) lines.push(currentLine);
                currentLine = word;
              }
            }
            if (currentLine) lines.push(currentLine);
            
            // Draw multiple lines
            lines.forEach((line, lineIndex) => {
              page.drawText(line, {
                x: labelX,
                y: labelY - (lineIndex * (labelFontSize + lineSpacing)), // Use lineSpacing between lines
                size: labelFontSize,
                font: font,
                color: rgb(0, 0, 0),
              });
            });
          } else {
            // Single line label
          page.drawText(labelText, {
            x: labelX,
            y: labelY,
            size: labelFontSize,
            font: font,
            color: rgb(0, 0, 0),
          });
          }
        }
        
        // Note: Height is calculated in the row height calculation above, 
        // so we don't need to adjust currentY here as it's handled by the caller
        break;

      case 'Date':
        // Create date text field with DD/MM/YYYY format
        const dateField = form.createTextField(fieldName);
        let dateValue = '';
        if (fieldValue) {
          try {
            const date = new Date(fieldValue);
            dateValue = format(date, 'dd/MM/yyyy');
          } catch (e) {
            console.warn('Could not format date value:', e);
            dateValue = fieldValue.toString();
          }
        }
        dateField.setText(dateValue);
        dateField.addToPage(page, {
          x: fieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: actualFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        try {
          dateField.setFontSize(defaultFontSize ?? 7); // Use configured font size or default to 7
        } catch (e) {
          console.warn('Could not set font size for date field:', e);
        }
        break;

      case 'DateTime':
        // Create date-time text field with DD/MM/YYYY HH:mm format
        const dateTimeField = form.createTextField(fieldName);
        let dateTimeValue = '';
        if (fieldValue) {
          try {
            const date = new Date(fieldValue);
            dateTimeValue = format(date, 'dd/MM/yyyy HH:mm');
          } catch (e) {
            console.warn('Could not format date-time value:', e);
            dateTimeValue = fieldValue.toString();
          }
        }
        dateTimeField.setText(dateTimeValue);
        dateTimeField.addToPage(page, {
          x: fieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: actualFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        try {
          dateTimeField.setFontSize(defaultFontSize ?? 7); // Use configured font size or default to 7
        } catch (e) {
          console.warn('Could not set font size for date-time field:', e);
        }
        break;

      case 'TextboxWithCheckbox':
        // Handle textbox with checkbox field type
        // Data format: { text: string, checked: boolean }
        let textboxValue = '';
        let checkboxValue = false;

        if (fieldValue && typeof fieldValue === 'object') {
          textboxValue = fieldValue.text || '';
          checkboxValue = fieldValue.checked || false;
        } else if (typeof fieldValue === 'string') {
          // Backward compatibility: treat string as just the text value
          textboxValue = fieldValue;
          checkboxValue = false;
        }

        // Split available width: most for text field, small portion for checkbox
        const checkboxWidth = 15; // Width allocated for checkbox
        const textFieldWidth = actualFieldWidth - checkboxWidth - 5; // 5px gap between text field and checkbox

        // Create text field
        let textboxField;
        try {
          textboxField = form.createTextField(`${fieldName}_text`);
        } catch (error: any) {
          if (error.message?.includes('already exists')) {
            textboxField = form.getTextField(`${fieldName}_text`);
          } else {
            throw error;
          }
        }
        textboxField.setText(textboxValue);
        textboxField.addToPage(page, {
          x: fieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: textFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        try {
          textboxField.setFontSize(fieldFontSize);
        } catch (e) {
          console.warn('Could not set font size for textbox field:', e);
        }

        // Create checkbox positioned next to the text field
        let checkboxField;
        try {
          checkboxField = form.createCheckBox(`${fieldName}_checkbox`);
        } catch (error: any) {
          if (error.message?.includes('already exists')) {
            checkboxField = form.getCheckBox(`${fieldName}_checkbox`);
          } else {
            throw error;
          }
        }
        checkboxField.addToPage(page, {
          x: fieldX + textFieldWidth + 5, // Position after text field with gap
          y: y - 13 + 8, // Align with text field (10pt box, 8pt offset)
          width: 10,
          height: 10,
          borderWidth: 1,
          borderColor: rgb(0, 0, 0),
        });

        // Check the checkbox if needed
        if (checkboxValue) {
          checkboxField.check();
        }
        break;

      case 'HorizontalRule':
        // Draw a horizontal line across the available width
        const lineY = y - (height / 6); // Center the line vertically in the field height
        const lineThickness = 1;
        const lineColor = rgb(0.7, 0.7, 0.7); // Match border color

        page.drawLine({
          start: { x: x, y: lineY },
          end: { x: x + width, y: lineY },
          thickness: lineThickness,
          color: lineColor,
        });
        break;

      default:
        // Default text field
        const defaultField = form.createTextField(fieldName);
        const defaultValue = formatWithAffixes(fieldValue);
        if (fieldPrefix || fieldSuffix) {
          console.log('PDF formatted value:', {
            code: field.code,
            type: normalizedFieldType,
            rawValue: fieldValue,
            formattedValue: defaultValue
          });
        }
        defaultField.setText(defaultValue);
        // Calculate input field position and width (accounting for prefix space)
        const defaultInputFieldX = fieldX + prefixWidth;
        const defaultInputFieldWidth = actualFieldWidth;
        
        defaultField.addToPage(page, {
          x: defaultInputFieldX,
          y: y - height + 8, // Adjust for label baseline (ascender)
          width: defaultInputFieldWidth,
          height: height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7),
        });
        if (!fieldValue && fieldSuffix) {
          drawAffixText(fieldSuffix, 'suffix', defaultInputFieldX, defaultInputFieldWidth);
        }
        if (!fieldValue && fieldPrefix) {
          drawAffixText(fieldPrefix, 'prefix', defaultInputFieldX, defaultInputFieldWidth);
        }
        try {
          defaultField.setFontSize(defaultFontSize ?? 7); // Use configured font size or default to 7
        } catch (e) {
          console.warn('Could not set font size for default field:', e);
        }
        break;
    }
  }

  /**
   * Renders the PDF using absolute positioning based on configuration (Pixel Version)
   */
  private static async renderPixelVersion(
    sections: any[],
    pdfDoc: PDFDocument,
    firstPage: PDFPage,
    headerConfigurationJson: string | undefined,
    headerHeight: number,
    pageMargin: number,
    headerData: any,
    font: PDFFont,
    boldFont: PDFFont,
    form: PDFForm,
    formData: any,
    pageWidth: number,
    pageHeight: number,
    defaultFontSize: number,
    labelFontSize: number,
    options: PDFGenerationOptions
  ): Promise<void> {
    console.log('Starting renderPixelVersion...');

    // Define items to render (Sections and Fields)
    interface PDFItem {
      type: 'field' | 'section';
      data: any; // field or section object
      config: any;
      pageNumber: number;
    }

    const allItems: PDFItem[] = [];

    sections.forEach(section => {
      // 1. Process Section Config (Border, Background)
      if (section.isActive) {
        const config = this.parseFieldConfiguration(section);
        // Priority: JSON config > DTO property > flat config
        // const pdfConfig = config.pdfConfig || section.pdfConfig || config;

        console.log(`[PixelVersion] Processing Section: ${section.description} (Code: ${section.code})`);
        console.log(`[PixelVersion]   Raw Config JSON:`, section.configurationJson);
        console.log(`[PixelVersion]   Parsed Config Keys:`, config ? Object.keys(config) : 'null');

        let pdfConfig: any = null;
        if (config && typeof config === 'object' && config.pdfConfig) {
          pdfConfig = { ...config, ...config.pdfConfig };
          console.log(`[PixelVersion] Section '${section.description}' loaded config from JSON_NESTED (merged)`);
        } else if (section.pdfConfig) {
          pdfConfig = { ...config, ...section.pdfConfig };
          console.log(`[PixelVersion] Section '${section.description}' loaded config from DTO_PROP (merged)`);
        } else if (config && (config.x !== undefined || config.y !== undefined)) {
          pdfConfig = config;
          console.log(`[PixelVersion] Section '${section.description}' loaded config from JSON_FLAT`);
        }

        if (pdfConfig) {
          // Parse page number safely
          const rawPageNum = pdfConfig.pageNumber;
          const pageNum = rawPageNum ? parseInt(rawPageNum.toString(), 10) : 1;

          if (!isNaN(pageNum) && pageNum > 0) {
            allItems.push({ type: 'section', data: section, config: pdfConfig, pageNumber: pageNum });
          }
        }
      }

      // 2. Process Fields in Section
      // 2. Process Fields in Section
      if (section.formFields) {
        section.formFields.forEach((field: any) => {
          if (field.isActive) {
            let config = this.parseFieldConfiguration(field);

            // Debug parsing for BIN_SYSTEMS
            if (field.code === 'BIN_SYSTEMS') {
              console.log(`[ConfigDebug] Raw configurationJson:`, field.configurationJson);
              console.log(`[ConfigDebug] Parsed config object keys:`, Object.keys(config));
            }

            // Priority logic:
            // 1. config.pdfConfig (from JSON string in DB) - "Source of Truth"
            // 2. field.pdfConfig (from DTO property) - Fallback
            // 3. config (flat properties in JSON) - Legacy/Fallback

            let pdfConfig: any = null;
            let configSource = 'unknown';

            // Check if 'config' has a nested 'pdfConfig' object
            if (config && typeof config === 'object' && config.pdfConfig && typeof config.pdfConfig === 'object') {
              pdfConfig = { ...config, ...config.pdfConfig };
              configSource = 'json_nested_merged';
            }
            // Check if field DTO has 'pdfConfig' property
            else if (field.pdfConfig && typeof field.pdfConfig === 'object') {
              pdfConfig = { ...config, ...field.pdfConfig };
              configSource = 'dto_property_merged';
            }
            // Fallback to flat config if it has coordinate properties
            else if (config && (config.x !== undefined || config.y !== undefined)) {
              pdfConfig = config;
              configSource = 'json_flat';
            }
            else {
              // Last resort
              pdfConfig = {};
              configSource = 'empty';
            }

            // Parse page number safely
            const rawPageNum = pdfConfig.pageNumber;
            const pageNum = rawPageNum ? parseInt(rawPageNum.toString(), 10) : 1;

            if (field.code === 'BIN_SYSTEMS') {
              console.log(`[ConfigDebug] BIN_SYSTEMS Source: ${configSource}`);
              console.log(`[ConfigDebug] Selected Y: ${pdfConfig.y}`);
            }

            allItems.push({ type: 'field', data: field, config: pdfConfig, pageNumber: (isNaN(pageNum) || pageNum < 1) ? 1 : pageNum });
          }
        });
      }
    });

    console.log(`Found ${allItems.length} active items for PixelVersion rendering`);

    // Group items by page number
    const itemsByPage: { [key: number]: PDFItem[] } = {};
    let maxPage = 1;

    allItems.forEach(item => {
      const pageNum = item.pageNumber;
      if (!itemsByPage[pageNum]) {
        itemsByPage[pageNum] = [];
      }
      itemsByPage[pageNum].push(item);
      if (pageNum > maxPage) maxPage = pageNum;
    });

    console.log(`Items grouped into max ${maxPage} pages`);

    // Iterate through pages
    for (let pageNum = 1; pageNum <= maxPage; pageNum++) {
      let page: PDFPage;

      if (pageNum === 1) {
        page = firstPage;
        // Fix for "Inverted/Upside Down" content on Page 1.
        // Ensure rotation is 0. 
        //page.setRotation(degrees(0));
      } else {
        page = pdfDoc.addPage([595, 842]);

        console.log('renderDynamicHeader: Drawing background rectangle addheader to page pageMargin:', pageMargin);
        await this.addHeaderToPage(
          page,
          pdfDoc,
          headerConfigurationJson,
          pageWidth,
          pageHeight,
          pageMargin,
          headerHeight,
          font,
          boldFont,
          headerData
        );
      }

      const pageItems = itemsByPage[pageNum] || [];
      // Sort: Sections first (background), then fields
      pageItems.sort((a, b) => {
        if (a.type === 'section' && b.type !== 'section') return -1;
        if (a.type !== 'section' && b.type === 'section') return 1;
        return 0;
      });

      console.log(`Rendering ${pageItems.length} items on page ${pageNum}`);

      for (const item of pageItems) {
        if (item.type === 'section') {
          await this.drawPixelVersionSection(page, item.data, item.config, pageHeight, boldFont);
        } else {

          // Normalize Global Y Coordinates
          // If Y > PageHeight, it implies a Global Coordinate was saved.
          // We map it to the current page.
          // If pageNum is 1, Y shouldn't be > pageHeight usually.
          // If pageNum is 2, Y might look like 897.
          // We want relative Y (55).
          // Formula: relativeY = globalY - (pageHeight * (pageNum - 1)).
          // But we need to use 'pageNum' derived from the ITEM, or the loop?
          // The item *is* in `itemsByPage[pageNum]`. So `pageNum` is correct context.

          // However, sometimes Y might be "810" on Page 2? (Bottom-aligned?)
          // No, "810" is < 842.
          // Only fix if > 842.

          // Start with simple modulo-like fix if plausible.
          // 897 on Page 2 (842 height) -> 55.
          if (item.config && typeof item.config.y === 'number' && item.config.y > pageHeight) {
            const originalY = item.config.y;
            item.config.y = item.config.y - (pageHeight * (pageNum - 1));

            console.log(`[PixelVersion] Normalized Global Y for ${item.data.code}: ${originalY} -> ${item.config.y}`);

            // Also fix Label Y if it exists and is Global
            if (item.config.labelConfig && typeof item.config.labelConfig.y === 'number' && item.config.labelConfig.y > pageHeight) {
              item.config.labelConfig.y = item.config.labelConfig.y - (pageHeight * (pageNum - 1));
            }
          }

          const debugY = item.config?.y !== undefined ? item.config.y : 'undefined';
          const debugX = item.config?.x !== undefined ? item.config.x : 'undefined';

          console.log(`[PixelVersion] Render Item ${item.data.code}: Page=${pageNum}, X=${debugX}, Y=${debugY}, PageHeight=${pageHeight}`);

          await this.addAbsoluteFieldToPDF(
            page,
            form,
            item.data,
            item.config,
            formData,
            font,
            boldFont,
            pageHeight,
            defaultFontSize,
            labelFontSize,
            options
          );
        }
      }
    }
  }

  /**
   * Draws a section rectangle/border
   */
  private static async drawPixelVersionSection(
    page: PDFPage,
    section: any,
    config: any,
    pageHeight: number,
    font: PDFFont
  ): Promise<void> {
    if (!config) return;

    const { x, y, width, height } = config;
    if (x === undefined || y === undefined || width === undefined || height === undefined) {
      console.warn(`[PixelVersion] Skipping Section draw due to missing coordinates: x=${x}, y=${y}, w=${width}, h=${height}`);
      return;
    }

    // Check if border should be shown: obey explicit "false", otherwise allow if true or if border width is positive
    const shouldShowBorder = config.showBorder !== false &&
      (config.showBorder === true || (config.borderWidth !== undefined && config.borderWidth > 0));

    console.log(`[PixelVersion] Drawing Section '${section?.description}' at ${x},${y} (${width}x${height}). ShowBorder=${shouldShowBorder}, BorderWidth=${config.borderWidth}`);

    const pdfY = pageHeight - y - height;

    // Determine if we have any fill color
    const fillColorString = config.backgroundColor || config.fillColor;
    const fillColor = fillColorString ? this.parseColor(fillColorString) : undefined;

    if (shouldShowBorder) {
      // Draw with border
      const borderWidth = config.borderWidth || 1;
      const borderColorString = config.borderColor || '#cccccc';
      const borderColor = this.parseColor(borderColorString);

      page.drawRectangle({
        x: x,
        y: pdfY,
        width: width,
        height: height,
        borderWidth: borderWidth,
        borderColor: borderColor,
        color: fillColor,
      });

      // Render section description as a label on the top-left border
      if (section && section.description && section.description.trim() !== '') {
        const description = section.description.trim();
        const fontSize = 10; // Small label size
        const textWidth = font.widthOfTextAtSize(description, fontSize);
        const textHeight = fontSize * 0.8; // Approximate cap height for centering

        // Background rectangle for text (to "cut" the border)
        const bgPaddingX = 4;
        const bgPaddingY = 2;
        const bgWidth = textWidth + (bgPaddingX * 2);
        const bgHeight = fontSize + (bgPaddingY * 2);

        // Position it on the top border
        // Top border is at pdfY + height
        const labelX = x + 10; // offset from left border
        const labelYCenter = pdfY + height;
        const labelRectY = labelYCenter - (bgHeight / 2);

        // Draw white background
        page.drawRectangle({
          x: labelX,
          y: labelRectY,
          width: bgWidth,
          height: 8,
          color: rgb(1, 1, 1), // White
        });

        // Draw centered text
        // Baseline adjustment: drawText Y is the baseline. 
        // To center it, we start from labelYCenter and subtract half the x-height/cap-height roughly.
        page.drawText(description, {
          x: labelX + bgPaddingX,
          y: labelYCenter - (textHeight / 2) - 1,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      }
    } else if (fillColor) {
      // Draw background only (no border)
      page.drawRectangle({
        x: x,
        y: pdfY,
        width: width,
        height: height,
        color: fillColor,
        borderWidth: 0
      });
    }
  }

  /**
   * Adds a field to the PDF using absolute positioning
   */
  private static async addAbsoluteFieldToPDF(
    page: PDFPage,
    form: PDFForm,
    field: any,
    config: any,
    formData: any,
    font: PDFFont,
    boldFont: PDFFont,
    pageHeight: number,
    defaultFontSize: number,
    labelFontSize: number,
    options: PDFGenerationOptions
  ): Promise<void> {
    const fieldType = this.normalizeFieldType(field.fieldType);

    // Ensure coordinates are numbers
    const x = Number(config.x) || 50;
    const y = Number(config.y) || 100;
    const width = Number(config.width) || 150;
    const height = Number(config.height) || 25;

    // Convert top-left Y (input) to bottom-left Y (PDF)
    // Input Y=0 is Top. PDF Y=0 is Bottom.
    const pdfY = pageHeight - y - height;

    console.log(`[PixelVersion] Rendering field ${field.code}: Input Y=${y} -> PDF Y=${pdfY} (PageHeight=${pageHeight})`);

    // Draw Label (if configured)
    if (config.labelConfig && config.labelConfig.isVisible !== false) {
      // Handle hideColon - default to showing colon unless explicitly hidden
      const hideColon = config.labelConfig.hideColon === true;
      const labelText = field.description || ''; // Pixel layouts do not need a colon by default

      const labelX = config.labelConfig.x !== undefined ? Number(config.labelConfig.x) : x;
      let labelY = config.labelConfig.y !== undefined ? Number(config.labelConfig.y) : y;
      const labelWidth = config.labelConfig.width !== undefined ? Number(config.labelConfig.width) : 100;
      const labelHeight = config.labelConfig.height !== undefined ? Number(config.labelConfig.height) : 20;

      const lFontSize = Number(config.labelConfig.fontSize) ||
        ((fieldType === 'Bool' || fieldType === 'CheckboxList') ? Number(config.fontSize) : 0) ||
        Number(labelFontSize) || 8;
      const lFont = config.labelConfig.isBold ? boldFont : font;
      const lColorString = config.labelConfig.fontColor || '#000000';
      const lColor = this.parseColor(lColorString);

      // Get alignment configuration (default to 'Left' for horizontal, 'Top' for vertical)
      const alignment = config.labelConfig.alignment || 'Left';
      const verticalAlignment = config.labelConfig.verticalAlignment || 'Top';  // Top, Middle, Bottom


      // Label Y Logic:
      // labelY is distance from Top of Page.
      // Text baseline should be roughly at (Top - labelY - fontSize).
      // We adjust slightly to align visually.

      // Heuristic Fix for "Label at Bottom" issue (e.g. LED_LIGHTS).
      // If LabelY (e.g. 810) is significantly different from Field Y (e.g. 50),
      // and LabelY is near bottom (> PageHeight/2), while Field is near top (< PageHeight/2),
      // Force LabelY to match Field Y.
      // Skip this fix when hideField is true, as the label should be positioned independently
      if (Math.abs(labelY - y) > 300 && config.hideField !== true) {
        console.warn(`[PixelVersion] Detected Label Deviation for ${field.code}! LabelY=${labelY}, FieldY=${y}. Fixing Label to ${y}.`);
        // We can't assign to const. Recalculate labelPdfY using 'y' instead of 'labelY'.
        const fixedLabelPdfY = pageHeight - y - lFontSize + 2;
        console.log(`[PixelVersion] Fixed Label Position for ${labelText}: PDF Y=${fixedLabelPdfY} (using FieldY=${y})`);

        // Truncate text if it exceeds the label width
        let truncatedText = labelText;
        let textWidth = lFont.widthOfTextAtSize(truncatedText, lFontSize);
        if (textWidth > labelWidth) {
          // Truncate character by character until it fits
          while (textWidth > labelWidth && truncatedText.length > 0) {
            truncatedText = truncatedText.slice(0, -1);
            textWidth = lFont.widthOfTextAtSize(truncatedText, lFontSize);
          }
          console.log(`[PixelVersion] Label truncated for ${field.code}: "${labelText}" -> "${truncatedText}"`);
        }

        // Calculate X position based on alignment
        let alignedX = labelX;
        if (alignment === 'Center') {
          alignedX = labelX + (labelWidth - textWidth) / 2;
        } else if (alignment === 'Right') {
          alignedX = labelX + labelWidth - textWidth;
        }

        // Calculate Y position based on vertical alignment
        // fixedLabelPdfY is baseline position at top of label area
        let verticallyAlignedY = fixedLabelPdfY;
        if (verticalAlignment === 'Middle') {
          // Center vertically: move down by half the remaining space after font
          verticallyAlignedY = fixedLabelPdfY - (labelHeight - lFontSize) / 2;
        } else if (verticalAlignment === 'Bottom') {
          // Align to bottom: move down by full remaining space
          verticallyAlignedY = fixedLabelPdfY - (labelHeight - lFontSize);
        }
        // 'Top' uses fixedLabelPdfY as-is

        // Use the fixed Y for drawing
        try {
          page.drawText(truncatedText, {
            x: alignedX,
            y: verticallyAlignedY,
            size: lFontSize,
            font: lFont,
            color: lColor,
          });
        } catch (err: any) { console.error(err); }

      } else {
        // Manual Multi-line Rendering to respect newlines and truncate instead of wrapping/squishing
        const lineHeight = lFontSize + 2;
        const lines = labelText.split(/\r?\n/);

        // Calculate total text height for vertical alignment
        const totalTextHeight = lines.length * lineHeight - 2; // Subtract spacing after last line

        // Calculate starting Y based on vertical alignment
        let startingPdfY = pageHeight - labelY - lFontSize + 2; // Default 'Top' alignment

        if (verticalAlignment === 'Middle') {
          // Center vertically: start higher by half the difference between label height and text height
          const verticalOffset = (labelHeight - totalTextHeight) / 2;
          startingPdfY = pageHeight - labelY - lFontSize + 2 - verticalOffset;
        } else if (verticalAlignment === 'Bottom') {
          // Bottom align: start at the bottom and work up
          const verticalOffset = labelHeight - totalTextHeight;
          startingPdfY = pageHeight - labelY - lFontSize + 2 - verticalOffset;
        }
        // 'Top' uses the default calculation

        let currentLinePdfY = startingPdfY;
        // Validate against bottom boundary: (pageHeight - labelY) is TOP of label box.
        // Bottom limit is (pageHeight - labelY - labelHeight).
        const backgroundBottomY = pageHeight - labelY - labelHeight;

        console.log(`[PixelVersion] Drawing Multiline Label for '${field.code}' at Top PDF Y=${currentLinePdfY}, Limit Y=${backgroundBottomY}, Alignment=${alignment}`);

        for (let i = 0; i < lines.length; i++) {
          // Check if next line fits vertically
          // The baseline is currentLinePdfY. The descent goes slightly below.
          // We'll use naive check: if baseline < backgroundBottomY + 2, stop.
          if (currentLinePdfY < backgroundBottomY) {
            console.log(`[PixelVersion] Label text truncated vertically at line ${i + 1}`);
            break;
          }

          let line = lines[i];
          // Truncate horizontally if too wide
          // Naive iterative truncation for safety (could be optimized)
          let textWidth = lFont.widthOfTextAtSize(line, lFontSize);
          if (textWidth > labelWidth) {
            while (textWidth > labelWidth && line.length > 0) {
              line = line.slice(0, -1);
              textWidth = lFont.widthOfTextAtSize(line, lFontSize);
            }
            // Recalculate textWidth after truncation
            textWidth = lFont.widthOfTextAtSize(line, lFontSize);
          }

          // Calculate X position based on alignment
          let alignedX = labelX;
          if (alignment === 'Center') {
            alignedX = labelX + (labelWidth - textWidth) / 2;
          } else if (alignment === 'Right') {
            alignedX = labelX + labelWidth - textWidth;
          }
          // alignment === 'Left' uses labelX as-is

          try {
            page.drawText(line, {
              x: alignedX,
              y: currentLinePdfY,
              size: lFontSize,
              font: lFont,
              color: lColor,
            });
          } catch (err) {
            console.error(`Error drawing label line ${i} for ${field.code}:`, err);
          }

          currentLinePdfY -= lineHeight;
        }
      }
    } else if (!config.labelConfig && fieldType !== 'CheckboxList') {
      // Default label logic if no config provided (fallback) - usually for development
      // Only if config.labelConfig IS undefined (not null, which might mean 'hide')
      if (config.labelConfig === undefined) {
        const labelText = (field.description || '') + ':';
        const lFontSize = ((fieldType === 'Bool' || fieldType === 'CheckboxList') ? Number(config.fontSize) : 0) ||
          Number(labelFontSize) || 8;
        // Default to placing label above field? Or at same Y?
        // User's default logic seems to imply label is separate object effectively.
        // If we rely on absolute positioning, we shouldn't guess.
        // But LED_LIGHTS example had no labelConfig and user saw labels.
        // So we MUST implement default label rendering here.

        // Default: Place label at slightly adjusted geometry relative to field X/Y?
        // Actually, if labelConfig is missing, we usually assume label is NOT drawn or handled by separate Label object?
        // BUT user said LED_LIGHTS (no labelConfig) was "showing at bottom".
        // This implies we ARE drawing it.
        // Let's assume we use 'y' as labelY.

        const labelPdfY = pageHeight - y - lFontSize + 2;
        console.log(`[PixelVersion] Drawing Default Label '${labelText}' at PDF Y=${labelPdfY} (using field Y=${y})`);

        page.drawText(labelText, {
          x: x,
          y: labelPdfY,
          size: lFontSize,
          font: font,
          color: rgb(0, 0, 0)
        });
      }
    }

    // Check if field should be hidden (show only label)
    if (config.hideField === true) {
      console.log(`[PixelVersion] Field ${field.code} hidden - showing only label`);
      return;
    }

    // Get value
    const rawValue = formData[field.code];
    // Use defaultValue if rawValue is undefined/null/empty string (but not false/0)
    let fieldValue = (rawValue !== undefined && rawValue !== null && rawValue !== '') ? rawValue :
      (config.defaultValue !== undefined ? config.defaultValue : '');

    // Handle parameterValue preloading if fieldValue is still empty
    if ((fieldValue === '' || fieldValue === undefined || fieldValue === null) && config.parameterValue) {
      const { source, property } = config.parameterValue;
      let dataSource: any = null;

      console.log('loading value for field', field.code, 'from', source, property);

      if (source === 'job') {
        dataSource = options.job;
        if (dataSource && property && dataSource[property] !== undefined && dataSource[property] !== null) {
          fieldValue = dataSource[property];
          console.log(`[PixelVersion] Preloaded parameter value for ${field.code} from ${source}.${property}: ${fieldValue}`);
        }
      } else if (source === 'customer') {
        dataSource = options.customer;
        if (dataSource && property && dataSource[property] !== undefined && dataSource[property] !== null) {
          fieldValue = dataSource[property];
          console.log(`[PixelVersion] Preloaded parameter value for ${field.code} from ${source}.${property}: ${fieldValue}`);
        }
      } else if (source === 'form') {
        if (options.formParameterValues && options.formParameterValues[field.id] !== undefined) {
          fieldValue = options.formParameterValues[field.id];
          console.log(`[PixelVersion] Preloaded parameter value for ${field.code} from ${source}: ${fieldValue}`);
        }
      } else if (source === 'UserDataField') {
        // Check if we have userDataFields passed in options
        if (options.userDataFields && options.userDataFields.length > 0) {
          const uField = options.userDataFields.find((f: any) => f.businessDataField?.code === property);
          if (uField) {
            fieldValue = uField.value;
            console.log(`[PixelVersion] Preloaded parameter value for ${field.code} from ${source}.${property}: ${fieldValue}`);
          } else {
            console.log(`[PixelVersion] UserDataField not found for ${property}`);
          }
        } else {
          // If not passed in options, we can't sync-fetch here. 
          // We rely on generateEditablePDF to have populated options.userDataFields.
          console.warn(`[PixelVersion] UserDataField source requested but options.userDataFields is empty. Fetch logic should be in generateEditablePDF.`);
        }
      }
    }

    // Create Widget
    // Based on fieldType
    switch (fieldType) {
      case 'Multiline':
        let multilineField;
        try { multilineField = form.createTextField(field.code); }
        catch { multilineField = form.getTextField(field.code); }
        multilineField.setText(fieldValue ? fieldValue.toString() : '');
        multilineField.enableMultiline();
        multilineField.addToPage(page, { x, y: pdfY, width, height, borderWidth: 1, borderColor: rgb(0.7, 0.7, 0.7) });
        try { multilineField.setFontSize(config.fontSize || defaultFontSize); } catch { }
        break;

      case 'Bool':
        let checkBox;
        try { checkBox = form.createCheckBox(field.code); }
        catch { checkBox = form.getCheckBox(field.code); }

        // Re-use boolean check logic
        let shouldCheck = false;
        if (typeof fieldValue === 'boolean') { shouldCheck = fieldValue === true; }
        else if (typeof fieldValue === 'string') { shouldCheck = fieldValue.toLowerCase() === 'true' || fieldValue === '1'; }
        else if (typeof fieldValue === 'number') { shouldCheck = fieldValue === 1; }
        else { shouldCheck = !!fieldValue; }

        // Checkbox size is usually small (10x10). Center it in the box.
        const cbSize = 10;
        // Center horizontally if width > cbSize
        const cbX = x + (width > cbSize ? (width - cbSize) / 2 : 0);
        // Center vertically
        const cbYComputed = pdfY + (height > cbSize ? (height - cbSize) / 2 : 0);

        checkBox.addToPage(page, {
          x: cbX,
          y: cbYComputed,
          width: cbSize,
          height: cbSize,
          borderWidth: 1,
          borderColor: rgb(0, 0, 0)
        });
        if (shouldCheck) checkBox.check();
        break;

      case 'CheckboxList':
        {
          const items = field.formFieldDataType?.formFieldDataItems?.filter((i: any) => i.isActive !== false) || [];
          const numColumns = config.checkboxColumns || options.checkboxListColumns || 3;

          // Handle Selected items
          let selectedCodes: string[] = [];
          if (fieldValue) {
            if (Array.isArray(fieldValue)) selectedCodes = fieldValue;
            else if (typeof fieldValue === 'string') {
              try { selectedCodes = JSON.parse(fieldValue); }
              catch { selectedCodes = fieldValue.split(',').map((s: string) => s.trim()); }
            }
          }

          // Render Logic
          const checkboxSize = 10;
          const colGap = 10;
          const colWidth = (width - (colGap * (numColumns - 1))) / numColumns;

          items.forEach((item: any, index: number) => {
            const colIndex = index % numColumns;
            const rowIndex = Math.floor(index / numColumns);

            // Calculate X
            const itemX = x + (colIndex * (colWidth + colGap));

            // Calculate Y - assume simple grid with 20pt rows
            const rowHeight = 20;
            // Start Y (top of field box)
            const startYTop = y;
            const itemYTop = startYTop + (rowIndex * rowHeight);

            // Convert to PDF Y (bottom-left)
            // Box for this item row: top=itemYTop, height=rowHeight.
            // PDF Y = pageHeight - itemYTop - rowHeight.
            // But let's center checkbox in the row.
            // Checkbox frame:
            const itemPdfY = pageHeight - itemYTop - rowHeight + (rowHeight - checkboxSize) / 2;

            // Checkbox Name
            const cbName = `${field.code}_${item.code}`;
            let cb;
            try { cb = form.createCheckBox(cbName); } catch { cb = form.getCheckBox(cbName); }

            cb.addToPage(page, {
              x: itemX,
              y: itemPdfY,
              width: 10,
              height: 10,
              borderWidth: 1,
              borderColor: rgb(0, 0, 0)
            });

            if (selectedCodes.includes(item.code)) cb.check();

            // Label
            const lX = itemX + 13;
            const itemFontSize = Number(config.fontSize) || 8;
            const labelMaxWidth = colWidth - 13;
            const labelText = item.description || '';
            const itemLineHeight = itemFontSize + 2;

            // Manual wrapping
            const lines = this.wrapText(labelText, labelMaxWidth, font, itemFontSize);

            // Calculate starting Y to center text block vertically on checkbox
            const totalTextHeight = lines.length * itemLineHeight;
            const firstLineBaseline = itemPdfY + 5 + (totalTextHeight / 2) - itemFontSize;

            lines.forEach((line, lineIdx) => {
              page.drawText(line, {
                x: lX,
                y: firstLineBaseline - (lineIdx * itemLineHeight),
                size: itemFontSize,
                font,
                color: rgb(0, 0, 0)
              });
            });
          });
        }
        break;

      case 'TextboxWithCheckbox':
        {
          // Handle textbox with checkbox field type
          // Data format: { text: string, checked: boolean }
          let tbValue = '';
          let cbValue = false;

          if (fieldValue && typeof fieldValue === 'object') {
            tbValue = fieldValue.text || '';
            cbValue = fieldValue.checked || false;
          } else if (typeof fieldValue === 'string') {
            // Backward compatibility: treat string as just the text value
            tbValue = fieldValue;
            cbValue = false;
          }

          // Split available width: most for text field, small portion for checkbox area
          const cbWidth = 20;
          const gap = 5;
          const totalTextboxWidth = width - cbWidth - gap;

          const tbFontSize = config.fontSize || defaultFontSize;
          const prefix = config.prefix || '';
          const suffix = config.suffix || '';

          // Calculate affix widths
          const prefixWidth = prefix ? font.widthOfTextAtSize(prefix, tbFontSize) + 2 : 0;
          const suffixWidth = suffix ? font.widthOfTextAtSize(suffix, tbFontSize) + 2 : 0;

          const actualTbWidth = Math.max(0, totalTextboxWidth - prefixWidth - suffixWidth);
          const actualTbX = x + prefixWidth;

          // 1. Create text field
          let tbField;
          try {
            tbField = form.createTextField(`${field.code}_text`);
          } catch (error: any) {
            tbField = form.getTextField(`${field.code}_text`);
          }

          tbField.setText(tbValue ? tbValue.toString() : '');
          tbField.addToPage(page, {
            x: actualTbX,
            y: pdfY,
            width: actualTbWidth,
            height: height,
            borderWidth: 1,
            borderColor: rgb(0.7, 0.7, 0.7),
          });

          // Draw prefixes/suffixes as labels
          if (prefix) {
            page.drawText(prefix, {
              x: x,
              y: pdfY + (height - tbFontSize) / 2 + 1,
              size: tbFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }
          if (suffix) {
            page.drawText(suffix, {
              x: actualTbX + actualTbWidth + 2,
              y: pdfY + (height - tbFontSize) / 2 + 1,
              size: tbFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }

          try {
            tbField.setFontSize(tbFontSize);
          } catch (e) {
            console.warn('Could not set font size for TextboxWithCheckbox text field:', e);
          }

          // 2. Create checkbox
          let cbField;
          const cbFieldName = `${field.code}_checkbox`;
          try {
            cbField = form.createCheckBox(cbFieldName);
          } catch (error: any) {
            cbField = form.getCheckBox(cbFieldName);
          }

          const cbSizeFinal = 10;
          const myCbX = x + totalTextboxWidth + gap + (cbWidth - cbSizeFinal) / 2;
          const myCbYComputed = pdfY + (height - cbSizeFinal) / 2;

          cbField.addToPage(page, {
            x: myCbX,
            y: myCbYComputed,
            width: cbSizeFinal,
            height: cbSizeFinal,
            borderWidth: 1,
            borderColor: rgb(0, 0, 0),
          });

          if (cbValue) {
            cbField.check();
          }
        }
        break;

      case 'Dimension':
        {
          // Parse dimension value (format: "H:value W:value D:value" or JSON)
          const parseDimVal = (val: string) => {
            if (!val) return { height: '', width: '', depth: '' };
            if (val.startsWith('{')) {
              try {
                const parsed = JSON.parse(val);
                return {
                  height: parsed.height || parsed.h || '',
                  width: parsed.width || parsed.w || '',
                  depth: parsed.depth || parsed.d || ''
                };
              } catch { }
            }
            const parts = val.split(' ');
            const result = { height: '', width: '', depth: '' };
            parts.forEach(part => {
              if (part.startsWith('H:')) result.height = part.substring(2);
              else if (part.startsWith('W:')) result.width = part.substring(2);
              else if (part.startsWith('D:')) result.depth = part.substring(2);
            });
            return result;
          };

          const dimensionValue = parseDimVal(fieldValue?.toString() || '');
          const dimensionLabels = ['H', 'W', 'D'];
          const vals = [dimensionValue.height, dimensionValue.width, dimensionValue.depth];

          const dFontSize = config.fontSize || defaultFontSize;
          const dGap = 5;
          const dPartWidth = (width - (dGap * 2)) / 3;

          for (let i = 0; i < 3; i++) {
            const dPartX = x + (i * (dPartWidth + dGap));
            const dSuffix = dimensionLabels[i];
            const dSuffixWidth = font.widthOfTextAtSize(dSuffix, dFontSize) + 2;
            const dTextFieldWidth = Math.max(0, dPartWidth - dSuffixWidth - 1);

            const dFieldName = `${field.code}_${dSuffix}`;
            let dField;
            try {
              dField = form.createTextField(dFieldName);
            } catch {
              dField = form.getTextField(dFieldName);
            }

            dField.setText((vals[i] !== undefined && vals[i] !== null) ? vals[i].toString() : '');
            dField.addToPage(page, {
              x: dPartX,
              y: pdfY,
              width: dTextFieldWidth,
              height: height,
              borderWidth: 1,
              borderColor: rgb(0.7, 0.7, 0.7),
            });
            try {
              dField.setFontSize(dFontSize);
            } catch { }

            page.drawText(dSuffix, {
              x: dPartX + dTextFieldWidth + 2,
              y: pdfY + (height - dFontSize) / 2 + 1,
              size: dFontSize,
              font: font,
              color: rgb(0, 0, 0)
            });
          }
        }
        break;

      case 'HorizontalRule':
        {
          // Draw a horizontal line across the available width
          // The thickness should equal the height of the field.
          const ruleY = pdfY + (height / 2); // Center of the field box in PDF coordinates

          page.drawLine({
            start: { x: x, y: ruleY },
            end: { x: x + width, y: ruleY },
            thickness: height,
            color: rgb(0.7, 0.7, 0.7), // Use gray for the rule to match standard dividers
          });
        }
        break;

      default:
        // Default text field
        let textField;
        try { textField = form.createTextField(field.code); }
        catch { textField = form.getTextField(field.code); }

        const fieldFontSize = config.fontSize || defaultFontSize;
        const prefix = config.prefix || '';
        const suffix = config.suffix || '';

        // Calculate heights/widths
        const prefixWidth = prefix ? font.widthOfTextAtSize(prefix, fieldFontSize) + 2 : 0;
        const suffixWidth = suffix ? font.widthOfTextAtSize(suffix, fieldFontSize) + 2 : 0;

        const actualFieldWidth = Math.max(0, width - prefixWidth - suffixWidth);
        const actualFieldX = x + prefixWidth;

        textField.setText(fieldValue ? fieldValue.toString() : '');
        textField.addToPage(page, {
          x: actualFieldX,
          y: pdfY,
          width: actualFieldWidth,
          height,
          borderWidth: 1,
          borderColor: rgb(0.7, 0.7, 0.7)
        });

        // Draw labels
        if (prefix) {
          page.drawText(prefix, {
            x: x,
            y: pdfY + (height - fieldFontSize) / 2 + 1,
            size: fieldFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }
        if (suffix) {
          page.drawText(suffix, {
            x: actualFieldX + actualFieldWidth + 2,
            y: pdfY + (height - fieldFontSize) / 2 + 1,
            size: fieldFontSize,
            font: font,
            color: rgb(0, 0, 0)
          });
        }

        try { textField.setFontSize(fieldFontSize); } catch { }
        break;
    }
  }
}
